import type { 
  Column, 
  EditorProps, 
  FormatterProps, 
  HeaderRendererProps
} from 'react-data-grid'
import { SelectColumn, SelectCellFormatter, TextEditor } from 'react-data-grid'
import { 
  AvgFwtByLot,
  BulkStatus,
  Department,
  DgtPdIAMUser,
  DgtPdOrderOperationLog,
  DgtPdOrderPicklist, 
  DgtPdOrderPicklistExtraReturn,
  DgtPdOrderPicklistMovement,
  ExtraReturnStatus,
  WasteRecordStatus,
} from '../models'
import { 
  OrderInspectionLog,
  OrderPicklistMovement,
  OrderPicklistMovementReport, 
  OrderReceipt,
  WorkCenter
} from '../components/types'
import RowClickable from '../components/RowClickable'
import { isFeatureEnabled } from './FeatureFlag'
import { formatNumber, formatDateFromUnix, formatNumberV2, formatNumberDigit, getMinuteTimeV2, formatDateISO, formatDate } from '../utilities/Format'
import { AiOutlineCheckCircle, AiFillCheckCircle, AiFillEdit, AiOutlineClose, AiFillCloseCircle, AiOutlineFieldNumber, AiOutlineCheck, AiOutlineSwap } from 'react-icons/ai'
import { GrNotes } from 'react-icons/gr'
import {
  BiCheckDouble,
  BiCabinet,
  BiTransfer,
  BiPackage
} from 'react-icons/bi'
import { 
  MdOutlineCancel, 
  MdKeyboardArrowDown, 
  MdKeyboardArrowUp, 
  MdSupervisedUserCircle, 
  MdModeEdit, 
  MdDelete,
  MdOutlineLibraryAdd,
  MdQuestionMark
} from 'react-icons/md'
import { HiDocumentText } from 'react-icons/hi'
import { SiMicrosoftoutlook, SiMicrosoftexcel } from 'react-icons/si'
import { IoMdAddCircle, IoMdColorFill } from 'react-icons/io'
import { RiBarcodeLine, RiQrCodeLine } from 'react-icons/ri'
import { FaCheckCircle } from 'react-icons/fa'
import { BulkBatch } from '../pages/Form/EditBatchNo'
import { format } from 'date-fns'
import EmployeeAuthEditor from '../components/EmployeeAuthEditor'
import CheckConfirmEditor from '../components/CheckConfirmEditor'
import QaCheckConfirmEditor from '../components/QaCheckConfirmEditor'
import WasteConfirmEditor from '../components/WasteConfirmEditor'
import NumberEditor from '../components/NumberEditor'
import Badge from '../components/Badge'
import { Badge as AmplifyBadge } from '@aws-amplify/ui-react';
import { PicklistMetaData, OrderPicklistExtraItem } from '../components/types'
import { Division, SHIFT } from '../constants/EnumType'
import { ProductionOrder } from '../pages/Masters/orders'
import { OperationTimeQC, OrderStatusDisplay, OrderStatusTheme, QCDowntimeCodes } from '../constants/Enum'
import { API } from 'aws-amplify'
import {
  getMaterial
} from '../graphql/queries'
import { OperationTime } from '../constants/Enum'
import CancelConfirmEditor from '../components/CancelConfirmEditor'
import RemoveUnusedMvEditor from '../components/RemoveUnusedMvEditor'
import UserEditor from '../components/UserEditor'
import InputReceiptReject from '../components/InputReceiptReject'

import differenceInDays from 'date-fns/differenceInDays'
import SelectStLoEditor from '../components/SelectStLoEditor'
import SelectShiftEditor from '../components/SelectShiftEditor'
import CopyReturnItemEditor from '../components/CopyReturnItemEditor'
import DeleteReturnItemEditor from '../components/DeleteReturnItemEditor'
import BulkOperationLogEditor from '../components/BulkOperationLogEditor'
import { OrderType } from '../models'
import ModalMvResEditor from '../components/ModalMvResEditor'
import SelectMvResCodeEditor from '../components/SelectMvResCodeEditor'

import { ImCancelCircle } from "react-icons/im";

import { Link } from 'react-router-dom'
import InputOtherReq from '../components/InputOtherReq'
import ShowInfoEditor from '../components/ShowInfoEditor'
import { MachineCategory } from '../models'

import RemoveUnusedInspectionEditor from '../components/RemoveUnusedInspectionEditor';
import QRCodeFGModal from '../components/QRCodeFGModal'

const BasePicklistColumn: readonly Column<DgtPdOrderPicklist>[] = [
  { key: 'pk', name: 'PD ORDER', width: 90, formatter: (props: FormatterProps<DgtPdOrderPicklist>) => {
    return <RowClickable value={`${props.row.pk}${props.row.extraReturnId ? '/extra/'+props.row.extraReturnId:''}`} label={props.row.pk}/>} },
  { key: 'material', name: 'MATERIAL', width: 320,
    cellClass: 'leading-[normal] flex flex-col justify-center gap-1',
    formatter: ({ row }: FormatterProps<DgtPdOrderPicklist>) => {
      return <RowClickable 
        value={`${row.pk}${row.extraReturnId ? '/extra/'+row.extraReturnId:''}`}
        label={
          <>
            <p className='font-bold text-black '>{row.materialDescription}</p>
            <p className='flex gap-1 text-muted'>
              {row.materialCode}
              {row.trialBatch && <p ><Badge variant={` text-[0.7rem] text-white bg-yellow-500`}>{row.trialBatch}</Badge></p>}
              {row.orderType && row.orderType === OrderType.REPACK && <p>
                <Badge variant={` text-[0.7rem] text-white bg-blue-500`}>{`${row.orderType}${row.alternativeText ? ` - ${row.alternativeText}` : ''}`}</Badge>
              </p>}
              {row.orderType && row.orderType === OrderType.CLAIM && <p>
                <Badge variant={` text-[0.7rem] text-white bg-red-500`}>{`${row.orderType}${row.alternativeText ? ` - ${row.alternativeText}` : ''}`}</Badge></p>}
            </p>
          </>
        }
      />
    }
  },
  { key: 'orderQty', name: 'จำนวน', width: 100,
    headerCellClass: ' text-right',
    cellClass: ' flex gap-1 justify-end', 
    formatter: ({ row }: FormatterProps<DgtPdOrderPicklist>) =>
      <>
        <span>{formatNumber(row.orderQty)}</span>
        <span className='text-[0.7rem] text-muted'>{row.materialUnit}</span>
      </>
  },
  { key: 'action', name: '',  width: 50, formatter: (props: FormatterProps<DgtPdOrderPicklist>) => {
    return <div className='flex items-center justify-center gap-1 text-blue-500'>
      <HiDocumentText /><RowClickable value={props.row.pk} label='ดูใบเบิก'/>
    </div> }
  }
]

export const PicklistApproveColumn: readonly Column<DgtPdOrderPicklist>[] = [
  { key: 'releasedDate', name: 'ออกเบิกเมื่อ',
    cellClass: 'leading-[normal] flex flex-col justify-center gap-1',
    formatter: ({ row }: FormatterProps<DgtPdOrderPicklist>) =>
      <>
        <p className='font-bold '>{formatDate(row.releasedDate)}</p>
        <p className='flex gap-1 text-muted'>{row.releasedBy}</p>
      </>
  },
  { key: 'requestedDateTime', name: 'อนุมัติเบิกเมื่อ',
    cellClass: 'leading-[normal] flex gap-1 h-full items-center',
    formatter: ({ row }: FormatterProps<DgtPdOrderPicklist>) => {
      if (!row.requestedBy) return <>-</>
      return <>
        <AiOutlineCheckCircle color='green' size={20}/>
        <div className='space-y-1 '>
          <p className='font-bold'>{formatDateFromUnix(row.requestedDateTime)}</p>
          <p className='flex gap-1 text-muted '>{row.requestedBy}</p>
        </div>
      </>
    }
  },
]

export const PicklistWithdrawColumn: readonly Column<DgtPdOrderPicklist>[] = [
  { key: 'requestedDateTime', name: 'อนุมัติเบิกเมื่อ',
    cellClass: row => {
      const baseClasses = 'leading-[normal] flex flex-col justify-center gap-1'
      if (row.approvedDateTime! > 0) return baseClasses

      const diffDays = differenceInDays(new Date(), new Date(row.requestedDateTime! * 1000))
      if (diffDays >= 4 && diffDays < 6) {
        return `${baseClasses} animate-pulse bg-yellow-300 font-bold`
      }
      else if (diffDays >= 6) {
        return `${baseClasses} animate-pulse bg-red-300 font-bold`
      }
      return baseClasses
    },
    formatter: ({ row }: FormatterProps<DgtPdOrderPicklist>) =>
      <>
        <p className='font-bold '>{formatDateFromUnix(row.requestedDateTime)}</p>
        <p className='flex gap-1 text-muted'>{row.requestedBy}</p>
      </>
  },
  { key: 'approvedDateTime', name: 'อนุมัติจ่ายโดย',
    cellClass: 'leading-[normal] flex gap-1 h-full items-center',
    formatter: ({ row }: FormatterProps<DgtPdOrderPicklist>) => {
      if (!row.approvedBy) return <>-</>
      return <>
        <AiOutlineCheckCircle color='green' size={20}/>
        <div className='space-y-1 '>
          <p className='font-bold'>{formatDateFromUnix(row.approvedDateTime)}</p>
          <p className='flex gap-1 text-muted '>{row.approvedBy}</p>
        </div>
      </>
    }
  },
  { key: 'kiNo', name : 'Ref NO', width: 85 },
  { key: 'commentText', name: 'หมายเหตุ', formatter: (props: FormatterProps<DgtPdOrderPicklist>) => {
    if (props.row.commentText?.includes('[เบิกเพิ่ม]')) {
      return <><Badge variant={`text-white bg-red-500`}>เบิกเพิ่ม</Badge>{props.row.commentText?.replace('[เบิกเพิ่ม]', '')}</>
    }
    return <>{props.row.commentText}</>
  }}
]

export const PicklistCheckOrderColumn: readonly Column<DgtPdOrderPicklist>[] = [
  { key: 'approvedDateTime', name: 'อนุมัติจ่ายเมื่อ',
    cellClass: 'leading-[normal] flex flex-col justify-center gap-1',
    formatter: ({ row }: FormatterProps<DgtPdOrderPicklist>) =>
      <>
        <p className='font-bold '>{formatDateFromUnix(row.approvedDateTime)}</p>
        <p className='flex gap-1 text-muted'>{row.approvedBy}</p>
      </>
  },
  { key: 'checkedDateTime', name: 'ตรวจสอบเมื่อ',
    cellClass: 'leading-[normal] flex gap-1 h-full items-center',
    formatter: ({ row }: FormatterProps<DgtPdOrderPicklist>) => {
      if (!row.checkedBy) return <>-</>
      return <>
        <AiOutlineCheckCircle color='green' size={20}/>
        <div className='space-y-1 '>
          <p className='font-bold'>{formatDateFromUnix(row.checkedDateTime)}</p>
          <p className='flex gap-1 text-muted '>{row.checkedBy}</p>
        </div>
      </>
    }
  },
  { key: 'commentText', name: 'หมายเหตุ' }
]

export const PicklistCheckMaterialColumn: readonly Column<DgtPdOrderPicklistMovement>[] = [
  { key: 'materialCode', name: 'รหัสสินค้า', width: 125, groupFormatter({ childRows, isExpanded }) {
    return <div className='flex items-center'>
      <span>{childRows[0].materialCode}</span>
      {isExpanded ? <MdKeyboardArrowUp size={20}/> : <MdKeyboardArrowDown size={20}/>}
    </div>
  } },
  { key: 'materialDescription', name: 'ชื่อ', frozen: true , formatter: (props: FormatterProps<DgtPdOrderPicklistMovement>) => {
    return <></>
  }, groupFormatter({ childRows }) {
    return <div>{childRows[0].materialDescription}</div>
  } },
  { key: 'orderId', name: 'PD ORDER', width: 90 },
  { key: 'requestQty', name: 'ปริมาณเบิก', width: 120, formatter: (props: FormatterProps<DgtPdOrderPicklistMovement>) => {
    return <div className='text-right '>{formatNumberV2(props.row.materialRequestQty)}</div>
  } },
  { key: 'amount', name: 'ปริมาณจ่าย', width: 100, formatter: (props: FormatterProps<DgtPdOrderPicklistMovement>) => {
    return <div className={`${props.row.amount < 0 ? ' text-red-400':' text-green-600'}`}>{
      formatNumberV2(props.row.amount)
    }</div>
  }, groupFormatter({ childRows }) {
    return <div className='font-bold '>{formatNumberV2(childRows.reduce((prev, { amount }) => prev + amount, 0))}</div>;
  } },
  { key: 'materialUnit', name: 'หน่วย', width: 50, minWidth: 50 },
  { key: 'batchLotNo', name: 'Batch No', width: 120 },
  { key: 'documentCreatedAt', name: 'เวลาจ่าย', width: 150, formatter: (props: FormatterProps<DgtPdOrderPicklistMovement>) => {
    return <div>{formatDateFromUnix(props.row.documentCreatedAt)}</div>
  } },
  { key: 'checkedBy', name: 'เวลาตรวจ', width: 150, formatter: (props: FormatterProps<DgtPdOrderPicklistMovement>) => {
    if (props.row.checkedDateTime)
      return <div>{formatDateFromUnix(props.row.checkedDateTime)}</div>
    return <div>-</div>
  }},
  {
    key: 'isChecked', name: 'ลงชื่อ', width: 100, 
      formatter: ({ row }: FormatterProps<DgtPdOrderPicklistMovement>) => {
      return <div className='flex items-center justify-center h-full'>
        <h6 className={`work-center-edit ${row.checkedDateTime ? 'text-red-600':'text-green-600'}`}>{row.checkedDateTime ? 'ยกเลิก':'ครบ ?'}</h6>
      </div>
    }, editor(props: EditorProps<DgtPdOrderPicklistMovement>) {
      return <CheckConfirmEditor props={props} isChecked={props.row.checkedDateTime! > 0}/>
    }, editorOptions: {
      editOnClick: true, commitOnOutsideClick: true
    }
  }
]

export const BasePicklistItemColumn: readonly Column<DgtPdOrderPicklist>[] = [
  { key: 'materialCode', name: 'รหัสสินค้า', width: 125 },
  { key: 'materialDescription', name: 'ชื่อ',
    formatter: (props: FormatterProps<DgtPdOrderPicklist>) => {
      return <div className='space-y-1 ' >
        <p>{props.row.materialDescription}</p>
        {props.row.barcodePkg && <AmplifyBadge size='small' variation="success"><RiBarcodeLine />{props.row.barcodePkg}</AmplifyBadge>}
      </div>
    },
    cellClass: "multi-line-row-data-grid"
  },
  { key: 'batchLotNo', name: 'BATCH', width: 120 },
  { key: 'requestQty', name: 'ปริมาณเบิก', width: 120, formatter: (props: FormatterProps<DgtPdOrderPicklist>) => {
    return <div className='text-right '>{formatNumber(props.row.requestQty)}</div>
  } },
  { key: 'materialUnit', name: 'หน่วย', width: 60, minWidth: 60 },
]

export const PicklistItemWithDrawColumn: readonly Column<DgtPdOrderPicklist>[] = [
  { key: 'withdrawQty', name: 'ปริมาณจ่าย', width: 100, formatter: (props: FormatterProps<DgtPdOrderPicklist>) => {
    const isEqual = props.row.requestQty === props.row.withdrawQty
    if ( isEqual ) {
      return <div className='flex items-center justify-center h-full'><AiFillCheckCircle color='green' size={20}/></div>
    } else {
      return <div className='text-right text-red-600'>{formatNumber(props.row.withdrawQty)}</div>
    }
  } }
]

export const PicklistItemCheckMvColumn: readonly Column<OrderPicklistMovement>[] = [
  { key: 'materialCode', name: 'รหัสสินค้า', width: 125, groupFormatter({ childRows, isExpanded, column }) {
    return <div className='flex items-center'>
      <span>{childRows[0].materialCode}</span>
      {isExpanded ? <MdKeyboardArrowUp size={20}/> : <MdKeyboardArrowDown size={20}/>}
    </div>
  }},
  { key: 'materialDescription', name: 'ชื่อ', frozen: true ,formatter: (props: FormatterProps<OrderPicklistMovement>) => {
    return <></>
  }, groupFormatter({ childRows }) {
    return <div className='leading-[normal] flex flex-col h-[100%] justify-center gap-1' >
      <p>{childRows[0].materialDescription}</p>
      {childRows[0].barcode && <AmplifyBadge size='small' variation="success" className='amplify-badge-w-fit'><RiBarcodeLine />{childRows[0].barcode}</AmplifyBadge>}
    </div>
    }
  },
  { key: 'requestQty', name: 'ปริมาณเบิก', width: 120, formatter: (props: FormatterProps<OrderPicklistMovement>) => {
    return <></>
  }, groupFormatter({ childRows }) {
    return <div>{formatNumber(childRows[0].materialRequestQty)}</div>
  } },
  { key: 'amount', name: 'ปริมาณจ่าย', width: 100, formatter: (props: FormatterProps<OrderPicklistMovement>) => {
    return <div>{formatNumber(props.row.amount)}</div>
  }, groupFormatter({ childRows }) {
    let total = childRows.reduce((prev, { amount }) => prev + amount, 0)
    if (!Number.isInteger(total)) total = Number(total.toFixed(3))
    const diff = total - (childRows[0].materialRequestQty || 0)
    return <div className={`${diff === 0 ? ' text-green-600': (diff > 0 ? ' text-blue-700':' text-red-400')} font-bold`}>{formatNumber(total)}</div>;
  } },
  { key: 'materialUnit', name: 'หน่วย', width: 60, minWidth: 60, groupFormatter({ childRows }) {
    return <>{childRows[0].materialUnit}</>
  } },
  { key: 'batchLotNo', name: 'Batch No', width: 120 },
  { key: 'documentCreatedAt', name: 'เวลาจ่าย', width: 150, formatter: (props: FormatterProps<OrderPicklistMovement>) => {
    return <div>{formatDateFromUnix(props.row.documentCreatedAt)}</div>
  } },
  { key: 'checkedBy', name: 'เวลาตรวจ', width: 150, formatter: (props: FormatterProps<OrderPicklistMovement>) => {
    if (props.row.checkedDateTime)
      return <div>{formatDateFromUnix(props.row.checkedDateTime)}</div>
    return <div>-</div>
  }},
  {
    key: 'isChecked', name: 'ลงชื่อ', width: 100, 
      formatter: ({ row }: FormatterProps<OrderPicklistMovement>) => {
      return <div className='flex items-center justify-center h-full'>
        <h6 className={`work-center-edit ${row.checkedDateTime ? 'text-red-600':'text-green-600'}`}>{row.checkedDateTime ? 'ยกเลิก':'ครบ ?'}</h6>
      </div>
    }, editor(props: EditorProps<OrderPicklistMovement>) {
      return <CheckConfirmEditor props={props} isChecked={props.row.checkedDateTime! > 0}/>
    }, editorOptions: {
      editOnClick: true, commitOnOutsideClick: true
    }
  }
]

export const PicklistCompleteColumn: readonly Column<DgtPdOrderPicklist>[] = [
  { key: 'recordedDateTime', name: 'บันทึกยอดเมื่อ',
    cellClass: 'leading-[normal] flex flex-col justify-center gap-1',
    formatter: ({ row }: FormatterProps<DgtPdOrderPicklist>) =>
      <>
        <p className='font-bold '>{formatDateFromUnix(row.recordedDateTime)}</p>
        <p className='flex gap-1 text-muted'>{row.recordedBy}</p>
      </>
  },
  { key: 'completedDateTime', name: 'ปิดเอกสารเมื่อ',
    cellClass: 'leading-[normal] flex gap-1 h-full items-center',
    formatter: ({ row }: FormatterProps<DgtPdOrderPicklist>) => {
      if (!row.completedBy) return <>-</>
      return <>
        <AiOutlineCheckCircle color='green' size={20}/>
        <div className='space-y-1 '>
          <p className='font-bold'>{formatDateFromUnix(row.completedDateTime)}</p>
          <p className='flex gap-1 text-muted '>{row.completedBy}</p>
        </div>
      </>
    }
  },
]

export const PicklistReturnColumn: readonly Column<DgtPdOrderPicklistExtraReturn>[] = [
  { key: 'id', name: 'เลขที่', width: 88, formatter: (props: FormatterProps<DgtPdOrderPicklistExtraReturn>) => {
    const orderInfo: DgtPdOrderPicklist = JSON.parse(props.row.orderInfo)
    let endpoint = window.location.pathname.replace('/', '')
    if (orderInfo.orderType === OrderType.REPACK) endpoint = 'good-reverse-order/repack'
    else if (orderInfo.orderType === OrderType.CLAIM) endpoint = 'good-reverse-order/claim'
    return <RowClickable value={props.row.id} label={props.row.id.slice(props.row.id.length - 8)} endpoint={endpoint} />
  }},
  { key: 'orderId', name: 'ORDER', width: 88, formatter: (props: FormatterProps<DgtPdOrderPicklistExtraReturn>) => {
    const orderInfo: DgtPdOrderPicklist = JSON.parse(props.row.orderInfo)
    let endpoint = window.location.pathname.replace('/', '')
    if (orderInfo.orderType === OrderType.REPACK) endpoint = 'good-reverse-order/repack'
    else if (orderInfo.orderType === OrderType.CLAIM) endpoint = 'good-reverse-order/claim'
    return <RowClickable value={props.row.id} label={props.row.orderId} endpoint={endpoint} />
  } },
  { key: 'orderInfo', name: 'รหัส Mat',
    cellClass: 'leading-[normal] flex flex-col justify-center gap-1',
    formatter: (props: FormatterProps<DgtPdOrderPicklistExtraReturn>) => {
    const orderInfo: DgtPdOrderPicklist = JSON.parse(props.row.orderInfo)
    let endpoint = window.location.pathname.replace('/', '')
    if (orderInfo.orderType === OrderType.REPACK) endpoint = 'good-reverse-order/repack'
    else if (orderInfo.orderType === OrderType.CLAIM) endpoint = 'good-reverse-order/claim'
    return <RowClickable endpoint={endpoint}
      value={props.row.id}
      label={
      <>
        <p className='font-bold text-black '>{orderInfo.materialDescription}</p>
        <p className='flex gap-1 text-muted'>
          {orderInfo.materialCode}
          {orderInfo.trialBatch && <p ><Badge variant={` text-[0.7rem] text-white bg-yellow-500`}>{orderInfo.trialBatch}</Badge></p>}
          {orderInfo.orderType && orderInfo.orderType === OrderType.REPACK && <p>
            <Badge variant={` text-[0.7rem] text-white bg-blue-500`}>{`${orderInfo.orderType}${orderInfo.alternativeText ? ` - ${orderInfo.alternativeText}` : ''}`}</Badge>
          </p>}
          {orderInfo.orderType && orderInfo.orderType === OrderType.CLAIM && <p>
            <Badge variant={` text-[0.7rem] text-white bg-red-500`}>{`${orderInfo.orderType}${orderInfo.alternativeText ? ` - ${orderInfo.alternativeText}` : ''}`}</Badge></p>}
        </p>
      </>
    } />
  }},
  { key: 'requestedBy', name: 'คืนโดย', width: 155,
    cellClass: 'leading-[normal] flex flex-col justify-center gap-1',
    formatter: ({ row }: FormatterProps<DgtPdOrderPicklistExtraReturn>) =>
      row.requestedBy ? <>
        <p className='font-bold '>{row.requestedBy}</p>
        <p className='flex gap-1 text-muted'>{formatDateFromUnix(row.requestedDateTime)}</p>
      </> : <>-</>
  },
  { key: 'approvedBy', name: 'รับคืนโดย', width: 155,
    cellClass: 'leading-[normal] flex flex-col justify-center gap-1',
    formatter: ({ row }: FormatterProps<DgtPdOrderPicklistExtraReturn>) =>
      row.approvedBy ? <>
        <p className='font-bold '>{row.approvedBy}</p>
        <p className='flex gap-1 text-muted'>{formatDateFromUnix(row.approvedDateTime)}</p>
      </> : <>-</>
  },
  { key: 'remark', name: 'Header Text', width: 250 },
  { key: 'documentNo', name: 'Mat. Doc', width: 105 },
  { key: 'status', name: 'สถานะ', width: 100, formatter: (props: FormatterProps<DgtPdOrderPicklistExtraReturn>) => {
    let badgeColor = ' bg-red-500'
    if (props.row.status === 'APPROVED') badgeColor = 'bg-green-500'
    else if (props.row.status === 'CANCELED') badgeColor = ' bg-gray-500'

    return <Badge variant={`${badgeColor} text-xs text-white`}>{props.row.status!}</Badge>
  }}
]

export const BulkBatchTransferColumn: readonly Column<BulkBatch>[] = [
  { key: 'currentBatchNo', name: 'Batch No ตั้งต้น', 
    headerCellClass: ' text-center', cellClass: 'text-center text-lg pt-2' },
  { key: 'newBatchNo', name: 'Batch No ที่ใช้ผลิต 10 หลัก', headerCellClass: ' text-center', 
    formatter: (props: FormatterProps<BulkBatch>) => {
      return <input title='batch_no'
        className='text-lg text-center input-form-text'
        onChange={(e) => props.onRowChange({...props.row, newBatchNo: e.currentTarget.value })}
        disabled={props.row.deleted}
        value={props.row.newBatchNo}
      />
    }
  },
  { key: 'fillToday', name: 'เติมวันนี้', headerCellClass: ' text-center', width: 80,
    formatter: ({ row, onRowChange, isCellSelected }: FormatterProps<BulkBatch>) => {
      if (row.currentBatchNo.includes('_') || row.newBatchNo.includes('_')) {
        return <SelectCellFormatter
          value={row.fillToday}
          onChange={() => {
            const isChecked = !row.fillToday
            const today = format(new Date(), 'ddMM')
            const newBatchNo = row.currentBatchNo.includes('_') ? `${today}${row.currentBatchNo.replace(/_/ig, '')}` : row.currentBatchNo
            onRowChange({ ...row, fillToday: isChecked, newBatchNo: isChecked ? newBatchNo : row.currentBatchNo });
          }}
          isCellSelected={isCellSelected}
        />
      }
      return <></>
    }
  },
  { key: 'bulkWithdraw', name: 'จ่ายโดย (Mix)', headerCellClass: ' text-center', cellClass: ' text-center',
    editor(props: EditorProps<BulkBatch>) {
      return <EmployeeAuthEditor props={props}/>
    },
    editable: (row) => !row.newBatchNo.includes('_') && row.newBatchNo !== '-',
    editorOptions: {
      editOnClick: true, commitOnOutsideClick: true
    },
    formatter: ({ row }: FormatterProps<BulkBatch>) => {
      if (row.approvedDatetime) return <div className='p-1 text-sm'>
        <div className='flex items-center justify-center gap-1 '>
          <AiOutlineCheckCircle color='green' size={20}/>{row.approvedBy}
        </div>
        <span className='text-xs text-muted'>({formatDateFromUnix(row.approvedDatetime)})</span>
      </div>
      const isDisabled = row.newBatchNo.includes('_') || row.newBatchNo === '-'
      return <h6 className={`pt-2 text-lg work-center-edit ${isDisabled ? ' text-disabled':''}`}>ลงชื่อจ่าย</h6>
    },
  },
  { key: 'bulkReceived', name: 'รับโดย (Fill)', headerCellClass: ' text-center', cellClass: ' text-center',
    formatter: ({ row, onRowChange }: FormatterProps<BulkBatch>) => {
      if (row.checkedDatetime) return <div className='p-1 text-sm'>
        <div className='flex items-center justify-center gap-1 '>
          <AiOutlineCheckCircle color='green' size={20}/>{row.checkedBy}
        </div>
        <span className='text-xs text-muted'>({formatDateFromUnix(row.checkedDatetime)})</span>
      </div>
      const isDisabled = row.newBatchNo.includes('_') || row.newBatchNo === '-'
      return <button className={`w-full text-lg underline ${isDisabled ? ' text-disabled':'text-red-400'}`}
        disabled={isDisabled}
        onClick={() => {
          onRowChange({ 
            ...row,
            checkedDatetime: Math.floor(Date.now() / 1000)
          })
        }}>
        ลงชื่อรับ
      </button>
    }
  },
  { key: 'qrCode', name: 'QR Code', headerCellClass: ' text-center', cellClass: 'flex justify-center items-center cursor-pointer', width: 100,
    formatter: ({ row }: FormatterProps<BulkBatch>) => {
      const pattern = /^\d{10}$/; // lot number 10 digits
      if (!pattern.test(row.newBatchNo || row.currentBatchNo)) return <>-</>
      return <>
        <RiQrCodeLine size={30}/>
      </>
    },
    editable: row => {
      const pattern = /^\d{10}$/; // lot number 10 digits
      return pattern.test(row.newBatchNo || row.currentBatchNo)
    },
    editor(props: EditorProps<BulkBatch>) {
      return <QRCodeFGModal props={props}/>
    },
    editorOptions: {
      editOnClick: true, commitOnOutsideClick: true
    }
  }
]

export const BulkBatchTransferColumnReadOnly: readonly Column<BulkBatch>[] = [
  { key: 'currentBatchNo', name: 'Batch No ตั้งต้น', headerCellClass: ' text-center', cellClass: 'text-center text-lg pt-2' },
  { key: 'newBatchNo', name: 'Batch No ที่ใช้ผลิต 10 หลัก', headerCellClass: ' text-center', cellClass: 'text-center text-lg pt-2' },
  { key: 'bulkWithdraw', name: 'จ่ายโดย (Mix)', headerCellClass: ' text-center', cellClass: ' text-center',
    formatter: ({ row }: FormatterProps<BulkBatch>) => {
      if (row.approvedDatetime) return <div className='p-1 text-sm'>
        <div className='flex items-center justify-center gap-1 '>
          <AiOutlineCheckCircle color='green' size={20}/>{row.approvedBy}
        </div>
        <span className='text-xs text-muted'>({formatDateFromUnix(row.approvedDatetime)})</span>
      </div>
      return <>-</>
    }
  },
  { key: 'bulkReceived', name: 'รับโดย (Fill)', headerCellClass: ' text-center', cellClass: ' text-center',
    formatter: ({ row }: FormatterProps<BulkBatch>) => {
      if (row.checkedDatetime) return <div className='p-1 text-sm'>
        <div className='flex items-center justify-center gap-1 '>
          <AiOutlineCheckCircle color='green' size={20}/>{row.checkedBy}
        </div>
        <span className='text-xs text-muted'>({formatDateFromUnix(row.checkedDatetime)})</span>
      </div>
      return <>-</>
    }
  }
]

export interface SummaryRow {
  total?: number,
  unit: string,
  totalRequest?: number,
  totalWithdraw?: number,
  totalUsedGood?: number,
  totalUsedWaste?: number,
  totalReturnGood?: number,
  totalReturnGoodShrink?: number,
  totalReturnWaste?: number,
  totalOtherQty?: number,
}

export interface SummaryLogRow {
  totalYield?: number,
  totalBulkUsed?: number,
}

export interface SummaryReceiptRow {
  dz?: number,
  ea?: number,
  bulkUsed?: number,
  retained?: number,
  micro?: number,
  validate?: number,
  shelfLife?: number,
  test?: number,
  other?: number,
  reject?: number,
  netEa?: number,
  yield?: number,
}

export const PicklistReturnWasteItemColumn: readonly Column<DgtPdOrderPicklistMovement>[] = [
  { key: 'batchLotNo', name: 'Batch No', width: 110, headerCellClass: 'text-center' },
  { key: 'returnWasteQty', name: 'คืนเสีย', width: 80, cellClass: 'text-right', headerCellClass: 'text-center' },
  { key: 'materialUnit', name: 'หน่วย', width: 80, cellClass: 'text-center', headerCellClass: 'text-center' },
  { key: 'wasteApprovedBy', name: 'อนุมัติโดย QC Line', headerCellClass: 'text-center' }
]

export const PicklistItemRecordColumn: readonly Column<OrderPicklistMovement, SummaryRow>[] = [
  { key: 'batchLotNo', name: 'Batch No', width: 105,
    summaryFormatter() {
      return <strong className='float-right '>จ่ายรวม</strong>
    }
  },
  { key: 'amount', name: 'จ่าย', formatter: (props: FormatterProps<OrderPicklistMovement, SummaryRow>) => {
    return <div className='text-right '>{props.row.movementType === '261' ? '' : '-'}{formatNumber(props.row.amount)}</div>
  }, summaryFormatter({ row }) {
    return <div className='font-bold text-right'>{formatNumber(row.total)}</div>
  }},
  { key: 'usedGoodQty', name: 'ใช้ดี', editorOptions: { editOnClick: true, commitOnOutsideClick: true },
    editor : (props: EditorProps<OrderPicklistMovement, SummaryRow>) => {
      return <NumberEditor onSubmit={(num) => props.onRowChange({...props.row, usedGoodQty: num}, true)} />
    },
    editable: row => row.amount > 0,
    headerRenderer: (props: HeaderRendererProps<OrderPicklistMovement, SummaryRow>) => {
      return <div className='flex items-center gap-1'>
        <AiOutlineCheckCircle color='green'/>
        {props.column.name}
      </div>
    },
    formatter: (props: FormatterProps<OrderPicklistMovement, SummaryRow>) => {
      if (props.row.usedGoodQty) {
        return <div className='flex items-center justify-between'>
          <span className={` font-bold ${props.row.usedGoodQty < 0 ? ' text-red-600':''}`}>{formatNumberV2(props.row.usedGoodQty)}</span>
          <AiFillEdit className=' text-[#007bff]' />
        </div>
      }
      return <div className='flex items-center justify-center h-full'><AiFillEdit className={props.row.amount > 0 ? ' text-[#007bff]':' text-muted'} /></div>
    }, 
    summaryFormatter({ row }) {
      return <div className='font-bold text-right text-green-600'>{formatNumber(row.totalUsedGood)}</div>
    }
  },
  { key: 'otherQty', name: 'โอนถ่าย', editorOptions: { editOnClick: true, commitOnOutsideClick: true },
    editor : (props: EditorProps<OrderPicklistMovement, SummaryRow>) => {
      return <NumberEditor onSubmit={(num) => props.onRowChange({...props.row, otherQty: num}, true)} />
    },
    editable: row => row.amount > 0,
    headerRenderer: (props: HeaderRendererProps<OrderPicklistMovement, SummaryRow>) => {
      return <div className='flex items-center gap-1'>
        <AiOutlineSwap color='orange'/>
        {props.column.name}
      </div>
    },
    formatter: (props: FormatterProps<OrderPicklistMovement, SummaryRow>) => {
      if (props.row.otherQty) {
        return <div className='flex items-center justify-between'>
          <span className={` font-bold ${props.row.otherQty < 0 ? ' text-red-600':''}`}>{formatNumberV2(props.row.otherQty)}</span>
          <AiFillEdit className=' text-[#007bff]' />
        </div>
      }
      return <div className='flex items-center justify-center h-full'><AiFillEdit className={props.row.amount > 0 ? ' text-[#007bff]':' text-muted'} /></div>
    }, 
    summaryFormatter({ row }) {
      return <div className='font-bold text-right text-amber-600'>{formatNumber(row.totalOtherQty)}</div>
    }
  },
  { key: 'usedWasteQty', name: 'ใช้เสีย', editorOptions: { editOnClick: true, commitOnOutsideClick: true },
    editor : (props: EditorProps<OrderPicklistMovement, SummaryRow>) => {
      return <NumberEditor onSubmit={(num) => props.onRowChange({...props.row, usedWasteQty: num}, true)} />
    },
    editable: row => row.amount > 0,
    headerRenderer: (props: HeaderRendererProps<OrderPicklistMovement, SummaryRow>) => {
      return <div className='flex items-center gap-1'>
        <MdOutlineCancel color='red'/>
        {props.column.name}
      </div>
    },
    formatter: (props: FormatterProps<OrderPicklistMovement, SummaryRow>) => {
      if (props.row.usedWasteQty) {
        return <div className='flex items-center justify-between'>
          <span className={`font-bold ${props.row.usedWasteQty < 0 ? ' text-red-600':''}`}>{formatNumberV2(props.row.usedWasteQty)}</span>
          <AiFillEdit className=' text-[#007bff]' />
        </div>
      }
      return <div className='flex items-center justify-center h-full'><AiFillEdit className={props.row.amount > 0 ? ' text-[#007bff]':' text-muted'} /></div> 
    }, 
    summaryFormatter({ row }) {
      return <div className='font-bold text-right text-red-600'>{formatNumber(row.totalUsedWaste)}</div>
    }
  },
  { key: 'returnGoodQty', name: 'คืนดีปกติ', editorOptions: { editOnClick: true, commitOnOutsideClick: true },
    headerCellClass: ' bg-blue-100 leading-[normal] flex items-center flex-col justify-center',
    headerRenderer: (props: HeaderRendererProps<OrderPicklistMovement, SummaryRow>) => {
      return <>
        <p>คืนดี</p>
        <p>ปกติ</p>
      </>
    },
    editor : (props: EditorProps<OrderPicklistMovement, SummaryRow>) => {
      return <NumberEditor onSubmit={(num) => props.onRowChange({...props.row, returnGoodQty: num}, true)} />
    },
    editable: row => row.amount > 0,
    formatter: (props: FormatterProps<OrderPicklistMovement, SummaryRow>) => {
      if (props.row.returnGoodQty) {
        return <div className='flex items-center justify-between'>
          <span className={`font-bold ${props.row.returnGoodQty < 0 ? ' text-red-600':''}`}>{formatNumberV2(props.row.returnGoodQty)}</span>
          <AiFillEdit className=' text-[#007bff]' />
        </div>
      }
      return <div className='flex items-center justify-center h-full'><AiFillEdit className={props.row.amount > 0 ? ' text-[#007bff]':' text-muted'} /></div>
    }, 
    summaryFormatter({ row }) {
      return <div className='font-bold text-right'>{formatNumber(row.totalReturnGood)}</div>
    }
  },
  { key: 'returnGoodShrinkQty', name: 'คืนดีติดSH/STK',
    editorOptions: { editOnClick: true, commitOnOutsideClick: true },
    headerCellClass: ' bg-blue-100 leading-[normal] flex items-center flex-col justify-center text-xs',
    headerRenderer: (props: HeaderRendererProps<OrderPicklistMovement, SummaryRow>) => {
      return <>
        <p>คืนดี</p>
        <p>ติดSH/STK</p>
      </>
    },
    editable: row => row.amount > 0,
    editor : (props: EditorProps<OrderPicklistMovement, SummaryRow>) => {
      return <NumberEditor onSubmit={(num) => props.onRowChange({...props.row, returnGoodShrinkQty: num}, true)} />
    },
    formatter: (props: FormatterProps<OrderPicklistMovement, SummaryRow>) => {
      if (props.row.returnGoodShrinkQty) {
        return <div className='flex items-center justify-between'>
          <span className={`font-bold`}>{formatNumberV2(props.row.returnGoodShrinkQty)}</span>
          <AiFillEdit className=' text-[#007bff]' />
        </div>
      }
      return <div className='flex items-center justify-center h-full'>
        <AiFillEdit className={props.row.amount! > 0 ? ' text-[#007bff]':' text-muted'} />
      </div>
    }, 
    summaryFormatter({ row }) {
      return <div className='font-bold text-right'>{formatNumber(row.totalReturnGoodShrink)}</div>
    }
  },
  { key: 'returnWasteQty', name: 'คืนเสีย',
    headerCellClass: ' bg-red-100',
    editorOptions: { editOnClick: true, commitOnOutsideClick: true },
    editor : (props: EditorProps<OrderPicklistMovement, SummaryRow>) => {
      return <NumberEditor onSubmit={(num) => props.onRowChange({...props.row, returnWasteQty: num}, true)} />
    },
    editable: row => row.amount > 0,
    formatter: (props: FormatterProps<OrderPicklistMovement, SummaryRow>) => {
      if (props.row.returnWasteQty) {
        return <div className='flex items-center justify-between'>
          <span className={`font-bold ${props.row.returnWasteQty < 0 ? ' text-red-600':''}`}>{formatNumberV2(props.row.returnWasteQty)}</span>
          <AiFillEdit className=' text-[#007bff]' />
        </div>
      }
      return <div className='flex items-center justify-center h-full'><AiFillEdit className={props.row.amount > 0 ? ' text-[#007bff]':' text-muted'} /></div>
    }, 
    summaryFormatter({ row }) {
      return <div className='font-bold text-right'>{formatNumber(row.totalReturnWaste)}</div>
    }
  },
  { 
    key: 'returnReasonCode',
    name: 'เหตุผลคืนPK',
    width: 150,
    cellClass: "multi-line-row-data-grid",
    editable: row => row.returnGoodQty! > 0 || row.returnGoodShrinkQty! > 0 || row.returnWasteQty! > 0,
    editorOptions: { editOnClick: true, commitOnOutsideClick: true },
    editor : (props: EditorProps<OrderPicklistMovement, SummaryRow>) => {
      return <ModalMvResEditor props={props} />
    },
    formatter: ({ row }: FormatterProps<OrderPicklistMovement, SummaryRow>) => {
      if (row.returnReasonCode) {
        return <div className='flex items-center justify-between w-full text-xs'>
          <span className={`font-bold`}>{row.returnReasonDescription}</span>
          <AiFillEdit className=' text-[#007bff]' />
        </div>
      }
      return <div className='flex items-center justify-center h-full'>
        <AiFillEdit className={row.returnGoodQty! > 0 || row.returnGoodShrinkQty! > 0 || row.returnWasteQty! > 0 ? ' text-[#007bff]':' text-muted'} />
      </div>
    }
  }
]

export const PicklistItemRecordColumnReadOnly: readonly Column<DgtPdOrderPicklistMovement, SummaryRow>[] = [
  { key: 'batchLotNo', name: 'Batch No', width: 120, summaryFormatter() {
    return <strong className='float-right '>จ่ายรวม</strong>
  } },
  { key: 'amount', name: 'จ่าย', width: 80, formatter: (props: FormatterProps<DgtPdOrderPicklistMovement, SummaryRow>) => {
    return <div className='text-right '>{props.row.movementType === '261' ? '' : '-'}{formatNumber(props.row.amount)}</div>
  }, summaryFormatter({ row }) {
    return <div className='font-bold text-right'>{formatNumber(row.total)}</div>
  }},
  { key: 'usedGoodQty', name: 'ใช้ดี',
    headerRenderer: (props: HeaderRendererProps<DgtPdOrderPicklistMovement, SummaryRow>) => {
      return <div className='flex items-center gap-1'>
        <AiOutlineCheckCircle color='green'/>
        {props.column.name}
      </div>
    },
    formatter: (props: FormatterProps<DgtPdOrderPicklistMovement, SummaryRow>) => {
      return <span className={` font-bold ${props.row.usedGoodQty! < 0 ? ' text-red-600':''}`}>{formatNumberV2(props.row.usedGoodQty)}</span>
    }, 
    summaryFormatter({ row }) {
      return <div className='font-bold text-right text-green-600'>{formatNumber(row.totalUsedGood)}</div>
    }
  },
  { key: 'otherQty', name: 'โอนถ่าย',
    headerRenderer: (props: HeaderRendererProps<DgtPdOrderPicklistMovement, SummaryRow>) => {
      return <div className='flex items-center gap-1'>
        <AiOutlineSwap color='orange'/>
        {props.column.name}
      </div>
    },
    formatter: (props: FormatterProps<DgtPdOrderPicklistMovement, SummaryRow>) => {
      return <span className={` font-bold ${props.row.otherQty! < 0 ? ' text-red-600':''}`}>{formatNumberV2(props.row.otherQty)}</span>
    }, 
    summaryFormatter({ row }) {
      return <div className='font-bold text-right text-green-600'>{formatNumber(row.totalOtherQty)}</div>
    }
  },
  { key: 'usedWasteQty', name: 'ใช้เสีย',
    headerRenderer: (props: HeaderRendererProps<DgtPdOrderPicklistMovement, SummaryRow>) => {
      return <div className='flex items-center gap-1'>
        <MdOutlineCancel color='red'/>
        {props.column.name}
      </div>
    },
    formatter: (props: FormatterProps<DgtPdOrderPicklistMovement, SummaryRow>) => {
      return <span className={`font-bold ${props.row.usedWasteQty! < 0 ? ' text-red-600':''}`}>{formatNumberV2(props.row.usedWasteQty)}</span>
    }, 
    summaryFormatter({ row }) {
      return <div className='font-bold text-right text-red-600'>{formatNumber(row.totalUsedWaste)}</div>
    }
  },
  { key: 'returnGoodQty', name: 'คืนดีปกติ',
    formatter: (props: FormatterProps<DgtPdOrderPicklistMovement, SummaryRow>) => {
      return <span className={`font-bold ${props.row.returnGoodQty! < 0 ? ' text-red-600':''}`}>{formatNumberV2(props.row.returnGoodQty)}</span>
    }, 
    summaryFormatter({ row }) {
      return <div className='font-bold text-right'>{formatNumber(row.totalReturnGood)}</div>
    }
  },
  { key: 'returnGoodShrinkQty', name: 'คืนดีติดSH/STK',
    formatter: (props: FormatterProps<DgtPdOrderPicklistMovement, SummaryRow>) => {
      return <span className={`font-bold ${props.row.returnGoodShrinkQty! < 0 ? ' text-red-600':''}`}>{formatNumberV2(props.row.returnGoodShrinkQty)}</span>
    }, 
    summaryFormatter({ row }) {
      return <div className='font-bold text-right'>{formatNumber(row.totalReturnGoodShrink)}</div>
    }
  },
  { key: 'returnWasteQty', name: 'คืนเสีย',
    formatter: (props: FormatterProps<DgtPdOrderPicklistMovement, SummaryRow>) => {
      return <span className={`font-bold ${props.row.returnWasteQty! < 0 ? ' text-red-600':''}`}>{formatNumberV2(props.row.returnWasteQty)}</span>
    }, 
    summaryFormatter({ row }) {
      return <div className='font-bold text-right'>{formatNumber(row.totalReturnWaste)}</div>
    }
  },
]

export const PicklistItemDeleteColumn: Column<DgtPdOrderPicklistMovement, SummaryRow> = {
  key: 'inactive', name: 'ลบ', width: 80,
  cellClass: row => `flex items-center justify-center${!row.amount || row.amount === 0 ? ' cursor-pointer' : ''}`,
  formatter: (props: FormatterProps<DgtPdOrderPicklistMovement, SummaryRow>) => {
    return !props.row.amount || props.row.amount === 0 ? 
      props.row.canceledDateTime! > 0 ? 
        <IoMdAddCircle size={20} className='text-green-700 '/> : <MdOutlineCancel color='red' size={20}/> : <></>
  },
  editable: row => !row.amount || row.amount === 0 ? true : false,
  editor : (props: EditorProps<DgtPdOrderPicklistMovement, SummaryRow>) => {
    return <RemoveUnusedMvEditor props={props} />
  },
  editorOptions: {
    editOnClick: true
  }
}

export const PicklistItemRecordTotalColumn: readonly Column<OrderPicklistMovement>[] = [
  { key: 'materialCode', name: 'รหัส Mat', width: 130, groupFormatter({ childRows, isExpanded }) {
    return <div className='leading-[normal] flex flex-col h-[100%] justify-center gap-1' >
      <div className='flex items-center'>
        <span>{childRows[0].materialCode}</span>
        {isExpanded ? <MdKeyboardArrowUp size={18}/> : <MdKeyboardArrowDown size={18}/>}
      </div>
      {childRows[0].barcode && <AmplifyBadge size='small' variation="success" className='amplify-badge-w-fit'>{childRows[0].barcode}</AmplifyBadge>}
    </div>
  } },
  { key: 'materialDescription', name: 'ชื่อ Mat', frozen: true ,
    formatter: (props: FormatterProps<OrderPicklistMovement>) => {
      return <></>
    }, groupFormatter({ childRows }) {
      return <div>{childRows[0].materialDescription}</div>
    }
  },
  { key: 'requestQty', name: 'เบิก', width: 80, frozen: true,
    formatter: (props: FormatterProps<OrderPicklistMovement>) => {
      return <></>
    }, groupFormatter({ childRows }) {
      const uniqueQtys = new Set(childRows.map(row => (row.materialRequestQty || 0)))
      const sumQty = Array.from(uniqueQtys).reduce((prev, qty) => prev + qty, 0)
      return childRows[0].materialType === 'BULK' ? 
        <div className='font-bold '>{formatNumberV2(childRows.reduce((prev, { materialRequestQty }) => prev + (materialRequestQty || 0), 0))}</div>
        : <div className='font-bold '>{formatNumberV2(sumQty)}</div>
    }
  },
  { key: 'withdrawQty', name: 'จ่าย', width: 80, frozen: true,
    formatter: (props: FormatterProps<OrderPicklistMovement>) => {
      return <></>
    }, groupFormatter({ childRows }) {
      return <div className='font-bold '>{formatNumberV2(childRows.reduce((prev, { amount }) => prev + amount, 0))}</div>
    }
  },
  { key: 'materialUnit', name: 'Unit', width: 45, minWidth: 45,
      formatter: (props: FormatterProps<OrderPicklistMovement>) => {
      return <></>
    }, groupFormatter({ childRows }) {
      return <div className='font-bold '>{childRows[0].materialUnit}</div>
    } 
  },
  { key: 'documentCreatedAt', name: 'เวลาจ่าย', width: 150, 
    formatter: (props: FormatterProps<OrderPicklistMovement>) => {
      return <div>{formatDateFromUnix(props.row.materialType === 'BULK' ? props.row.approvedDateTime: props.row.documentCreatedAt)}</div>
    }, groupFormatter({ childRows }) {
      return <div className='font-bold '>{childRows.length > 1 ? '' : formatDateFromUnix(
        childRows[0].materialType === 'BULK' ? childRows[0].approvedDateTime: childRows[0].documentCreatedAt
      )}</div>
    }
  },
  { key: 'usedGoodQty', name: 'ใช้ดี', width: 90,
    headerCellClass: ' text-right',
    formatter: (props: FormatterProps<OrderPicklistMovement>) => {
      return <div className='text-right ' >{formatNumberV2(props.row.usedGoodQty)}</div>
    }, groupFormatter({ childRows }) {
      return <div className='font-bold text-right text-green-500'>{
        formatNumberV2(childRows.reduce((prev, { usedGoodQty }) => prev + usedGoodQty!, 0))
      }</div>
    }
  },
  { key: 'otherQty', name: 'โอนถ่าย', width: 80,
    headerCellClass: ' text-right',
    formatter: (props: FormatterProps<OrderPicklistMovement>) => {
      return <div className='text-right ' >{formatNumberV2(props.row.otherQty)}</div>
    }, groupFormatter({ childRows }) {
      return <div className='font-bold text-right text-green-500'>{
        formatNumberV2(childRows.reduce((prev, { otherQty }) => prev + otherQty!, 0))
      }</div>
    }
  },
  { key: 'usedWasteQty', name: 'ใช้เสีย', width: 80,
    headerCellClass: ' text-right',
    formatter: (props: FormatterProps<OrderPicklistMovement>) => {
      return <div className='text-right ' >{formatNumberV2(props.row.usedWasteQty)}</div>
    }, groupFormatter({ childRows }) {
      return <div className='font-bold text-right text-red-500'>{
        formatNumberV2(childRows.reduce((prev, { usedWasteQty }) => prev + usedWasteQty!, 0))
      }</div>
    }
  },
  { key: 'returnGoodQty', name: 'คืนดี', width: 80,
    headerCellClass: ' text-right',
    formatter: (props: FormatterProps<OrderPicklistMovement>) => {
      return <div className='text-right ' >{formatNumberV2(props.row.returnGoodQty)}</div>
    }, groupFormatter({ childRows }) {
      return <div className='font-bold text-right text-gray-500'>{
        formatNumberV2(childRows.reduce((prev, { returnGoodQty }) => prev + returnGoodQty!, 0))
      }</div>
    }
  },
  { key: 'returnGoodShrinkQty', name: 'คืนดีติดSH/STK', width: 80,
    headerCellClass: ' leading-[normal] flex items-end flex-col justify-center text-xs',
    headerRenderer: (props: HeaderRendererProps<OrderPicklistMovement>) => {
      return <>
        <p>คืนดี</p>
        <p>ติดSH/STK</p>
      </>
    },
    formatter: (props: FormatterProps<OrderPicklistMovement>) => {
      return <div className='text-right ' >{formatNumberV2(props.row.returnGoodShrinkQty)}</div>
    }, groupFormatter({ childRows }) {
      return <div className='font-bold text-right text-gray-500'>{
        formatNumberV2(childRows.reduce((prev, { returnGoodShrinkQty }) => prev + returnGoodShrinkQty!, 0))
      }</div>
    }
  },
  { key: 'returnWasteQty', name: 'คืนเสีย', width: 80,
    headerCellClass: ' text-right',
    formatter: (props: FormatterProps<OrderPicklistMovement>) => {
      return <div className='text-right ' >{formatNumberV2(props.row.returnWasteQty)}</div>
    }, groupFormatter({ childRows }) {
      return <div className='font-bold text-right text-gray-500'>{
        formatNumberV2(childRows.reduce((prev, { returnWasteQty }) => prev + returnWasteQty!, 0))
      }</div>
    }
  },
  { key: 'batchLotNo', name: 'Batch No', width: 120,
    groupFormatter({ childRows }) {
      return <div className='font-bold'>{childRows.length === 1 ? childRows[0].batchLotNo : '' }</div>
    }
  }
]

export const PicklistItemSummaryColumn: readonly Column<DgtPdOrderPicklistMovement, SummaryRow>[] = [
  { key: 'batchLotNo', name: 'Batch No', width: 120, summaryFormatter() {
    return <strong className='float-right '>Total</strong>
  } },
  { key: 'amount', name: 'จ่าย',
    formatter: (props: FormatterProps<DgtPdOrderPicklistMovement, SummaryRow>) => {
      return <div className='text-right'>{formatNumberV2(props.row.amount)}{props.row.materialType}</div>
    },
    summaryFormatter({ row }) {
      return <div className='font-bold text-right'>{formatNumber(row.total)}</div>
    }
  },
  { key: 'usedGoodQty', name: 'ใช้ดี',
    formatter: (props: FormatterProps<DgtPdOrderPicklistMovement, SummaryRow>) => {
      return <div className='text-right'>{formatNumberV2(props.row.usedGoodQty)}</div>
    },
    summaryFormatter({ row }) {
      return <div className='font-bold text-right text-green-600'>{formatNumber(row.totalUsedGood)}</div>
    }
  },
  { key: 'usedWasteQty', name: 'ใช้เสีย',
    formatter: (props: FormatterProps<DgtPdOrderPicklistMovement, SummaryRow>) => {
      return <div className='text-right'>{formatNumberV2(props.row.usedWasteQty)}</div>
    },
    summaryFormatter({ row }) {
      return <div className='font-bold text-right text-red-600'>{formatNumber(row.totalUsedWaste)}</div>
    }
  }, {
    key: 'returnGoodQty', name: 'คืนดี',
    formatter: (props: FormatterProps<DgtPdOrderPicklistMovement, SummaryRow>) => {
      return <div className='text-right'>{formatNumberV2(props.row.returnGoodQty)}</div>
    },
    summaryFormatter({ row }) {
      return <div className='font-bold text-right text-gray-600'>{formatNumber(row.totalReturnGood)}</div>
    }
  },
  { key: 'returnWasteQty', name: 'คืนเสีย',
    formatter: (props: FormatterProps<DgtPdOrderPicklistMovement, SummaryRow>) => {
      return <div className='text-right'>{formatNumberV2(props.row.returnWasteQty)}</div>
    },
    summaryFormatter({ row }) {
      return <div className='font-bold text-right'>{formatNumber(row.totalReturnWaste)}</div>
    }
  }
]

export const PicklistItemReturnConfirmColumn: readonly Column<DgtPdOrderPicklistMovement>[] = [
  { key: 'materialCode', name: 'รหัส Mat', width: 120 },
  { key: 'materialDescription', name: 'Desc' },
  {
    key: 'returnGoodQty', name: 'คืนดีปกติ', width: 80,
    headerCellClass: ' text-right',
    formatter: (props: FormatterProps<DgtPdOrderPicklistMovement>) => {
      return <div className='text-right'>{formatNumberV2(props.row.returnGoodQty)}</div>
    }
  },
  {
    key: 'returnGoodShrinkQty', name: 'คืนดีติดSH/STK', width: 80,
    formatter: (props: FormatterProps<DgtPdOrderPicklistMovement>) => {
      return <div className='text-right'>{formatNumberV2(props.row.returnGoodShrinkQty)}</div>
    },
    headerCellClass: ' leading-[normal] flex items-center gap-1 justify-end',
    headerRenderer: (props: HeaderRendererProps<DgtPdOrderPicklistMovement>) => {
      return <div>
        <p>คืนดีติด</p>
        <p>SH/STK</p>
      </div>
    },
  },
  { key: 'returnWasteQty', name: 'คืนเสีย', minWidth: 60, width: 60,
    headerCellClass: ' text-right',
    formatter: (props: FormatterProps<DgtPdOrderPicklistMovement>) => {
      return <div className='text-right'>{formatNumberV2(props.row.returnWasteQty)}</div>
    }
  },
  { key: 'batchLotNo', name: 'Batch No', width: 105 },
  { key: 'returnReasonCode', name: 'เหตุผล', width: 170,
    formatter: ({ row }: FormatterProps<DgtPdOrderPicklistMovement>) => {
      return <>{row.returnReasonDescription}</>
    }
  }
]

export const PicklistReturnItemColumn: readonly Column<OrderPicklistExtraItem | null>[] = [
  { key: 'materialCode', name: 'รหัส', width: 120 },
  { key: 'materialDescription', name: 'คำอธิบาย' },
  { key: 'qty', name: 'คืนดีปกติ', width: 80,
    headerCellClass: ' bg-green-100 leading-[normal] flex items-center gap-1 justify-center',
    headerRenderer: (props: HeaderRendererProps<OrderPicklistExtraItem | null>) => {
      return <>
        <AiFillCheckCircle className='text-green-600 ' size={16} />
        <div>
          <p>คืนดี</p>
          <p>ปกติ</p>
        </div>
      </>
    },
    formatter: (props: FormatterProps<OrderPicklistExtraItem | null>) => {
      return props.row?.qty! > 0 ? <div className='flex items-center justify-between h-full'>
        <span className='text-right text-green-600' >{formatNumberV2(props.row?.qty)}</span>
        <AiFillEdit className=' text-[#007bff]' />
      </div> : null
    },
    editable: row => row?.qty! > 0,
    editor(props: EditorProps<OrderPicklistExtraItem | null>) {
      return <TextEditor {...props} />
    },
    editorOptions: { editOnClick: true, commitOnOutsideClick: true }
  },
  { key: 'withShQty', name: 'คืนดีติดSH/STK', width: 90,
    formatter: (props: FormatterProps<OrderPicklistExtraItem | null>) => {
      return props.row?.withShQty! > 0 ? <div className='flex items-center justify-between h-full'>
        <span className='text-right text-green-600' >{formatNumberV2(props.row?.withShQty)}</span>
        <AiFillEdit className=' text-[#007bff]' />
      </div> : null
    },
    headerCellClass: ' bg-green-100 leading-[normal] flex items-center gap-1 justify-center',
    headerRenderer: (props: HeaderRendererProps<OrderPicklistExtraItem | null>) => {
      return <>
        <AiFillCheckCircle className='text-green-600 ' size={16} />
        <div>
          <p>คืนดีติด</p>
          <p>SH/STK</p>
        </div>
      </>
    },
    editable: row => row?.withShQty! > 0,
    editor(props: EditorProps<OrderPicklistExtraItem | null>) {
      return <TextEditor {...props} />
    },
    editorOptions: { editOnClick: true, commitOnOutsideClick: true }
  },
  { key: 'wasteQty', name: 'คืนเสีย', width: 80,
    formatter: (props: FormatterProps<OrderPicklistExtraItem | null>) => {
      return props.row?.wasteQty! > 0 ? <div className='flex items-center justify-between h-full'>
        <span className='text-right text-red-600' >{formatNumberV2(props.row?.wasteQty)}</span>
        <AiFillEdit className=' text-[#007bff]' />
      </div> : null
    },
    headerCellClass: 'flex items-center gap-1 bg-red-100',
    headerRenderer: (props: HeaderRendererProps<OrderPicklistExtraItem | null>) => {
      return <><AiFillCloseCircle className='text-red-600 ' size={16} />{props.column.name}</>
    },
    editable: row => row?.wasteQty! > 0,
    editor(props: EditorProps<OrderPicklistExtraItem | null>) {
      return <TextEditor {...props} />
    },
    editorOptions: { editOnClick: true, commitOnOutsideClick: true }
  },
  { key: 'otherQty', name: 'โอนถ่าย', width: 80,
    headerCellClass: 'flex items-center gap-1',
    headerRenderer: (props: HeaderRendererProps<OrderPicklistExtraItem | null>) => {
      return <><BiTransfer size={16} />{props.column.name}</>
    },
  },
  { key: 'materialUnit', name: 'Unit', minWidth: 50, width: 50 },
  { 
    key: 'returnReasonCode',
    name: 'เหตุผลคืน',
    headerCellClass: 'flex items-center gap-1',
    headerRenderer: (props: HeaderRendererProps<OrderPicklistExtraItem | null>) => {
      return <><MdQuestionMark size={16} />{props.column.name}</>
    },
    width: 170,
    editable: row => row?.returnReasonCode !== null && row?.returnReasonCode !== '',
    editorOptions: { editOnClick: true, commitOnOutsideClick: true },
    editor : (props: EditorProps<OrderPicklistExtraItem | null>) => {
      return <SelectMvResCodeEditor props={props} />
    },
    formatter: ({ row, column }: FormatterProps<OrderPicklistExtraItem | null>) => {
      if (row?.returnReasonCode) {
        return <div className='flex items-center justify-between w-full'>
          <span >{row.returnReasonDescription}</span>
          {column.editable ? <AiFillEdit className=' text-[#007bff] hover:cursor-pointer' /> : null}
        </div>
      }
      return <></>
    }
  },
  { key: 'batchLotNo', name: 'Batch No', width: 130,
    groupFormatter({ childRows }) {
      return <div className='font-bold'>{childRows.length === 1 ? childRows[0]?.batchLotNo : '' }</div>
    },
    headerCellClass: 'flex items-center gap-1',
    headerRenderer: (props: HeaderRendererProps<OrderPicklistExtraItem | null>) => {
      return <><AiOutlineFieldNumber />{props.column.name}</>
    },
    editor: TextEditor,
    editable: true,
    editorOptions: { editOnClick: true, commitOnOutsideClick: true },
    formatter: (props: FormatterProps<OrderPicklistExtraItem | null>) => {
      return props.column.editable ? <div className='flex items-center justify-between h-full'>
        <span className={props.row?.batchLotNo ? '' : ' text-red-500 italic'}>{props.row?.batchLotNo || 'กรุณากรอก Lot'}</span>
        <AiFillEdit className=' text-[#007bff] hover:cursor-pointer' />
      </div> : <div className='flex items-center justify-center'>
        <span >{props.row?.batchLotNo!}</span>
      </div>
    }
  },
  { key: 'returnStorageLocation', name: 'ช่องคืน', width: 100, cellClass: ' text-center',
    editable: row => row?.withShQty! > 0 || (row?.manualLocation || false),
    editorOptions: {
      editOnClick: true, commitOnOutsideClick: true
    },
    editor(props: EditorProps<OrderPicklistExtraItem | null>) {
      return <SelectStLoEditor props={props} />
    },
    headerCellClass: 'flex items-center gap-1',
    headerRenderer: (props: HeaderRendererProps<OrderPicklistExtraItem | null>) => {
      return <><BiCabinet />{props.column.name}</>
    },
    formatter: ({ row, column }: FormatterProps<OrderPicklistExtraItem | null>) => {
      if (row?.withShQty! > 0 || row?.manualLocation) {
        if (row?.returnStorageLocation && row.returnStorageLocation !== '-') {
          return <div className='flex items-center justify-center h-full gap-2'>
            <span className='font-bold '>{row?.returnStorageLocation}</span>
            {column.editable ? <AiFillEdit className=' text-[#007bff] cursor-pointer' /> : null}
          </div>
        }
        return <div className='italic work-center-edit '>เลือก Location</div>
      }
      if (row?.wasteQty! > 0) {
        return <span className='font-bold '>{row?.returnStorageLocation}</span>
      }
      return row?.manualLocation ? <></> : <div className='font-bold'>{row?.returnStorageLocation ? row?.returnStorageLocation : 'Loc เดิม'}</div>
    }
  },
  { key: 'manualSLOC', name: 'เลือกเอง', minWidth: 70, width: 70, cellClass: ' p-1',
    formatter: (props: FormatterProps<OrderPicklistExtraItem | null>) => {
      const { row, isCellSelected } = props
      if (row?.qty! > 0) {
        const isChecked = row?.manualLocation || false
        return <SelectCellFormatter
          value={isChecked}
          onChange={() => {
            const currentRow = { 
              ...row,
              manualLocation : !row?.manualLocation
            } as OrderPicklistExtraItem | null
            props.onRowChange(currentRow);
          }}
          isCellSelected={isCellSelected}
        />
      }
      return <></>
    },
  },
  { key: 'remark', name: 'Note', width: 240,
    headerCellClass: 'flex items-center gap-1',
    headerRenderer: (props: HeaderRendererProps<OrderPicklistExtraItem | null>) => {
      return <><GrNotes />{props.column.name}<span className='italic text-muted'>(จำกัด 50 ตัว)</span></>
    },
    editor: TextEditor,
    editable: true,
    editorOptions: { editOnClick: true, commitOnOutsideClick: true },
    formatter: (props: FormatterProps<OrderPicklistExtraItem | null>) => {
      const isExceedLength = props.row?.remark?.length! > 50
      return props.column.editable ? <div className='flex items-center justify-between h-full'>
        <span className={isExceedLength ? ' text-red-500' : ''} >{isExceedLength ? '(เกิน 50 ตัว)':''}{props.row?.remark}</span>
        <AiFillEdit className=' text-[#007bff]' />
      </div> : <div className='flex items-center justify-center'>
        <span className={isExceedLength ? ' text-red-500' : ''} >{props.row?.remark!}</span>
      </div>
    }
  },
  { key: 'delete', name: 'ลบ', minWidth: 50, width: 50,
    headerCellClass: ' text-center',
    cellClass: ' flex items-center justify-center h-full cursor-pointer hover:bg-blue-100',
    formatter: (props: FormatterProps<OrderPicklistExtraItem | null>) => {
      return props.row?.active === false ? 
      <IoMdAddCircle size={20} className='text-green-500 ' /> : 
      <MdDelete size={20} className='text-red-500 ' />
    },
    editor(props: EditorProps<OrderPicklistExtraItem | null>) {
      return props.column.editable ? <DeleteReturnItemEditor props={props} /> : null
    },
    editable: true,
    editorOptions: { editOnClick: true, commitOnOutsideClick: true }
  },
  { key: 'copy', name: 'เพิ่ม', minWidth: 50, width: 50,
    headerCellClass: ' text-center',
    cellClass: ' flex items-center justify-center h-full cursor-pointer hover:bg-blue-100',
    formatter: (props: FormatterProps<OrderPicklistExtraItem | null>) => {
      return <MdOutlineLibraryAdd size={20} className='text-slate-500 ' />
    },
    editor(props: EditorProps<OrderPicklistExtraItem | null>) {
      return props.column.editable ? <CopyReturnItemEditor props={props} /> : null
    },
    editable: true,
    editorOptions: { editOnClick: true, commitOnOutsideClick: true }
  }
]

export const PicklistExtraColumn: readonly Column<DgtPdOrderPicklistExtraReturn>[] = [
  { key: 'Items', name: 'รายการเบิก', formatter: (props: FormatterProps<DgtPdOrderPicklistExtraReturn>) => {
    const items: PicklistMetaData[] = props.row.items ? JSON.parse(props.row.items) : []
    return <div className='p-1 space-y-2'>
      {items.map(itm => <p key={itm.materialCode} className='text-base'>
      &#8226; {itm.materialCode} - {itm.materialDescription} = <span className='font-bold '>{itm.qty} {itm.materialUnit}</span>
      </p>)}
    </div>
  }, cellClass: "multi-line-row-data-grid" },
  { key: 'requestedBy', name: 'ขอเบิกโดย', width: 150, formatter: (props: FormatterProps<DgtPdOrderPicklistExtraReturn>) => {
    if (props.row.requestedBy) return <div className='flex flex-col items-center justify-center gap-1'>
        <span>{props.row.requestedBy}</span>
        <span>{formatDateFromUnix(props.row.requestedDateTime)}</span>
      </div>
    return <div className='text-center '>-</div>
  }, cellClass: "multi-line-row-data-grid-center" },
  { key: 'approvedBy', name: 'อนุมัติเบิกโดย', width: 150, formatter: (props: FormatterProps<DgtPdOrderPicklistExtraReturn>) => {
    if (props.row.approvedBy) return <div className='flex flex-col items-center justify-center gap-1'>
      <span>{props.row.approvedBy}</span>
      <span>{formatDateFromUnix(props.row.approvedDateTime)}</span>
    </div>
    return <div className='text-center '>-</div>
  }, cellClass: "multi-line-row-data-grid-center" },
  { key: 'status', name: 'สถานะ', width: 120, formatter: (props: FormatterProps<DgtPdOrderPicklistExtraReturn>) => {
    let badgeColor = ' bg-red-500'
    if (props.row.status === ExtraReturnStatus.APPROVED) badgeColor = 'bg-green-500'
    else if (props.row.status === ExtraReturnStatus.WITHDRAWED) badgeColor = 'bg-green-500'
    else if (props.row.status === ExtraReturnStatus.CHECKED) badgeColor = 'bg-green-700'
    else if (props.row.status === ExtraReturnStatus.CANCELED) badgeColor = 'bg-gray-900'
    else if (props.row.status === ExtraReturnStatus.QA_CHECKED) badgeColor = 'bg-amber-500'

    return <Badge variant={`${badgeColor} text-xs text-white`}>{props.row.status!}</Badge>
  }},
  { key: 'remark', name: 'หมายเหตุ' }
]

export const PicklistItemExtraChoiceColumn: readonly Column<DgtPdOrderPicklist>[] = [
  SelectColumn,
  { key: 'materialCode', name: 'รหัสสินค้า', width: 125,
    editable: row => (row.externalBom || false),
    editorOptions: {
      editOnClick: true, commitOnOutsideClick: true
    },
    editor: MaterialEditor,
    formatter: ({ row }: FormatterProps<DgtPdOrderPicklist>) => {
      if (!row.materialCode) return <div className='italic '>คลิกเพื่อกรอกรหัส</div>
      return <div>{row.materialCode}</div>
    }
  },
  { key: 'materialDescription', name: 'ชื่อ' },
  { key: 'requestQty', name: 'จำนวน', width: 80,
    editable: row => row.materialDescription ? true : false,
    editor : (props: EditorProps<DgtPdOrderPicklist>) => {
      return <NumberEditor onSubmit={(num) => props.onRowChange({...props.row, requestQty: num}, true)} />
    },
    editorOptions: {
      editOnClick: true, commitOnOutsideClick: true
    },
    formatter: ({ row }: FormatterProps<DgtPdOrderPicklist>) => {
      return <div className='text-right '>{formatNumberV2(row.requestQty)}</div>
    }
  },
  { key: 'materialUnit', name: 'หน่วย', width: 80 }
]

export const ProductionOrderColumn: readonly Column<ProductionOrder>[] = [
  { key: 'id', name: 'ORDER#', width: 100 },
  { key: 'division', name: 'Division', width: 120, formatter: (props: FormatterProps<ProductionOrder>) => {
    let badgeColor = 'bg-gray-400'
    if (props.row.division === 'Toiletry') badgeColor = 'bg-sky-400'
    else if (props.row.division === 'Household') badgeColor = 'bg-rose-400'
    return <div className='flex items-center justify-between h-full cursor-pointer'>
        <Badge variant={` ${badgeColor} text-xs text-white`}>{props.row.division || 'ไม่ระบุ'}</Badge>
        <AiFillEdit className=' text-[#007bff]' />
      </div>
  }, editor(props: EditorProps<ProductionOrder>) {
    const { row, onRowChange } = props
    return <div className='flex items-center justify-center w-full h-full'>
      <select title='select'
        value={row.division || 'ไม่ระบุ'}
        className='p-1 border border-gray-400 rounded-md'
        onChange={(e) => {
          const updatedRow = { ...row, 
            division: e.currentTarget.value
          }
          onRowChange(updatedRow, true)
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        {Object.keys(Division).map( division => <option key={division} value={division}>{division}</option>)}
      </select>
    </div>
  }, editorOptions: {
    editOnClick: true, commitOnOutsideClick: true
  }},
  { key: 'materialType', name: 'Type', width: 80 },
  { key: 'materialCode', name: 'Code', width: 100 },
  { key: 'materialDescription', name: 'Description' },
  { key: 'orderQty', name: 'Qty', width: 100, formatter: (props: FormatterProps<ProductionOrder>) => {
    return <div className='text-right '>{formatNumberV2(props.row.orderQty)} {props.row.materialUnit}</div>
  } },
  { key: 'statusFromApp', name: 'Status', width: 100, formatter: ({ row }) => {
    return <Badge variant={getValueOrderStatusThemeEnum(row.statusFromApp!)}>{getValueOrderStatusDisplayEnum(row.statusFromApp!)}</Badge>
  }},
  { key: 'createdAt', name: 'สร้างเมื่อ', width: 130, formatter: ({ row }: FormatterProps<ProductionOrder>) => {
    return <div >{format(new Date(row.createdAt), 'dd.MM.yyyy HH:mm')}</div>
  } }
]

function getValueOrderStatusThemeEnum(value: string) {
  return Object.entries(OrderStatusTheme).find(([key, val]) => key === value)?.[1]
    || value;
}

function getValueOrderStatusDisplayEnum(value: string) {
  return Object.entries(OrderStatusDisplay).find(([key, val]) => key === value)?.[1]
    || value;
}

export const getColunmPicklist = (exts : readonly Column<any>[], overrideClickCol?: Column<any>) : readonly Column<any>[] => {
  const columns = Array.from(BasePicklistColumn)
  columns.splice(columns.length - 1, 0, ...Array.from(exts))
  if (overrideClickCol) {
    // remove last col/insert override
    columns.splice(columns.length - 1, 1, overrideClickCol)
  }
  return columns
}

function autoFocusAndSelect(input: HTMLInputElement | null) {
  input?.focus();
  input?.select();
}

export default function MaterialEditor<DgtPdOrderPicklist>({
  row,
  column,
  onRowChange,
  onClose
}: EditorProps<DgtPdOrderPicklist>) {
  const commitChange = async (value : string) => {
    onRowChange({ ...row, [column.key]: value }, true)
    const getResult: any = await API.graphql({
      query: getMaterial,
      variables: { material_code: value }
    })
    const returnResult = getResult.data?.getMaterial
    const material = JSON.parse(returnResult)

    onRowChange({
      ...row, [column.key]: value,
      materialDescription: material ? material['materialDescription'] : '',
      materialUnit: material ? material['materialUnit'] : '',
      materialType: material ? (material['materialType'] === 'ZPKG' ? 'PKG' : 'BULK') : ''
    }, true)
  }
  return (
    <input title='s'
      className='pl-2 '
      ref={autoFocusAndSelect}
      onBlur={(e) => {
        e.preventDefault()
        commitChange(e.currentTarget.value)
      }}
      onKeyDown={(e) => {
        if (e.code === 'Enter') {
          e.preventDefault()
          commitChange(e.currentTarget.value)
        }
      }}
    />
  );
}

export const OperationLogsColumns: readonly Column<DgtPdOrderOperationLog, SummaryLogRow>[] = [
  {
    key: 'check', name: '', width: 50, minWidth: 50, cellClass: ' p-1',
    formatter({ row, onRowChange, isCellSelected }) {
      const isChecked = row.confirmedDate ? true : false
      return <SelectCellFormatter
        disabled={row.confirmationId ? true : false}
        value={isChecked}
        onChange={() => {
          onRowChange({ ...row, confirmedDate: isChecked ? undefined : new Date().toISOString()  });
        }}
        isCellSelected={isCellSelected}
      />
    }
  },
  // ปิด flag แล้วคอลัมน์ต้องกลับเป็นแบบเดิม (แคบ, ตรงกลาง, ไม่มี description) เพราะ log downtime เก่าก็โดนด้วย
  { key: 'timeType', name: 'Type',
    width: isFeatureEnabled('SETUP_REASON_CODE') ? 341 : 110,
    headerCellClass: isFeatureEnabled('SETUP_REASON_CODE') ? ' text-left' : ' text-center',
    cellClass: isFeatureEnabled('SETUP_REASON_CODE') ? ' text-left' : ' text-center',
    formatter: (props: FormatterProps<DgtPdOrderOperationLog, SummaryLogRow>) => {
      let badgeColor = ''
      if (props.row.timeType === OperationTime.SETUP_TIME) {
        badgeColor = ' bg-gray-400'
      }
      else if (props.row.timeType === OperationTime.RUN_TIME) {
        badgeColor = ' bg-blue-400'
      }
      else if (props.row.timeType === OperationTime.SOAP_BASE) {
        badgeColor = ' bg-orange-400'
      }
      else if (props.row.timeType === OperationTime.DOWN_TIME) {
        badgeColor = ' bg-red-600'
      }
      
      // downtime carries its own code, Set Up carries the reason code - show "code - desc" either way
      const showReasonCode = isFeatureEnabled('SETUP_REASON_CODE')
      const description = props.row.downtimeCode ? props.row.downtimeDescription : props.row.reasonDescription
      const descSuffix = showReasonCode && description ? ` - ${description}` : ''
      const setupCode = showReasonCode && !props.row.downtimeCode && props.row.reasonCode
        ? ` ${props.row.reasonCode}${descSuffix}` : ''

      if (QCDowntimeCodes.includes(props.row.downtimeCode || '')) {
        let bulkStatusColor = 'bg-red-600'
        if (props.row.bulkStatus === BulkStatus.PASS) bulkStatusColor = 'bg-green-500'
        if (props.row.bulkStatus === BulkStatus.HOLD) bulkStatusColor = 'bg-yellow-500'
        if (props.row.bulkStatus === BulkStatus.REJECT) bulkStatusColor = 'bg-red-500'
        return <Badge variant={` ${bulkStatusColor} text-xs text-white`}>{`D${props.row.downtimeCode}${props.row.bulkStatus ? `-${props.row.bulkStatus}` : ''}${descSuffix}`}</Badge>
      }
      return <Badge variant={` ${badgeColor} text-xs text-white`}>{props.row.timeType === OperationTime.DOWN_TIME ? `Down ${props.row.downtimeCode}${descSuffix}` : `${props.row.timeType}${setupCode}`}</Badge>
    }
  },
  { key: 'batchNo', name: 'Batch No', width: 120, headerCellClass: ' text-center', cellClass: ' text-center',
    editor: TextEditor,
    editable: true,
    editorOptions: { editOnClick: true, commitOnOutsideClick: true },
    formatter: (props: FormatterProps<DgtPdOrderOperationLog, SummaryLogRow>) => {
      return <div className='flex items-center justify-between h-full'>
        <span >{props.row.batchNo || ''}</span>
        {props.column.editable ? <AiFillEdit className=' text-[#007bff]' /> : null}
      </div>
    }
  },
  { key: 'mch', name: 'Mch', width: 60, minWidth: 60, headerCellClass: ' text-center', cellClass: ' text-center', sortable: true,
    editor: TextEditor,
    editable: row => row.timeType === OperationTimeQC.INSPECT_BULK ? false : true,
    editorOptions: { editOnClick: true, commitOnOutsideClick: true },
    formatter: (props: FormatterProps<DgtPdOrderOperationLog, SummaryLogRow>) => {
      return <div className='flex items-center justify-between h-full'>
        <span >{props.row.mch}</span>
        {props.column.editable ? <AiFillEdit className=' text-[#007bff]' /> : null}
      </div>
    }
  },
  { key: 'split', name: 'Split', width: 80, cellClass: ' text-center', sortable: true,
    editor(props: EditorProps<DgtPdOrderOperationLog, SummaryLogRow>) {
      return <NumberEditor placeholder='' onSubmit={(num) => props.onRowChange({...props.row, split: num}, true)} />
    },
    editable: true,
    editorOptions: { editOnClick: true, commitOnOutsideClick: true },
    formatter: (props: FormatterProps<DgtPdOrderOperationLog, SummaryLogRow>) => {
      return <div className='flex items-center justify-between h-full'>
        <span >{props.row.split}</span>
        {props.column.editable ? <AiFillEdit className=' text-[#007bff]' /> : null}
      </div>
    }
  },
  { key: 'postingDate', name: 'Posting Date', headerCellClass: ' text-center', cellClass: ' text-center', width: 120, 
    formatter: (props: FormatterProps<DgtPdOrderOperationLog, SummaryLogRow>) => {
      return <>{format(new Date(props.row.postingDate), 'dd-MM-yyyy')}</>
    }
  },
  { key: 'shift', name: 'Shift', width: 80, headerCellClass: ' text-center', cellClass: ' text-center',
    editor(props: EditorProps<DgtPdOrderOperationLog, SummaryLogRow>) {
      return <SelectShiftEditor props={props} />
    },
    editable: row => row.timeType === OperationTimeQC.INSPECT_BULK ? false : true,
    editorOptions: { editOnClick: true, commitOnOutsideClick: true },
    formatter: (props: FormatterProps<DgtPdOrderOperationLog, SummaryLogRow>) => {
      const matchShift = Object.entries(SHIFT).find(([, val]) => val.value === props.row.shift)
      return <div className='flex items-center justify-between h-full'>
        <span >{matchShift && matchShift.length > 1 ? matchShift[1].display : '-'}</span>
        {props.column.editable ? <AiFillEdit className=' text-[#007bff]' /> : null}
      </div>
    }
  },
  { key: 'startedTime', name: 'เริ่ม', width: 60, minWidth: 60, headerCellClass: ' text-center', cellClass: ' text-center', 
    formatter: (props: FormatterProps<DgtPdOrderOperationLog, SummaryLogRow>) => {
      return <>{props.row.startedTime ? format(new Date(props.row.startedTime * 1000), 'HH:mm') : '-'}</>
    } 
  },
  { key: 'finishedTime', name: 'จบ', width: 60, minWidth: 60, headerCellClass: ' text-center', cellClass: ' text-center', 
    formatter: (props: FormatterProps<DgtPdOrderOperationLog, SummaryLogRow>) => {
      return <>{props.row.finishedTime ? format(new Date(props.row.finishedTime * 1000), 'HH:mm') : '-'}</>
    }
  },
  {
    key: 'totalTime', name: 'เวลารวม', width: 130, headerCellClass: ' text-center', cellClass: ' text-center', 
    formatter: (props: FormatterProps<DgtPdOrderOperationLog, SummaryLogRow>) => {
      if (props.row.timeType === OperationTime.DOWN_TIME) {
        if (!props.row.totalTime) return <>-</>
        if (!props.row.downtimeBaseTime) return <>{`${getMinuteTimeV2(props.row.startedTime, props.row.finishedTime)} นาที`}</>
        return <div>
          <span className="text-gray-500 line-through decoration-red-500">{getMinuteTimeV2(props.row.startedTime, props.row.finishedTime)} นาที</span>
          {` ${(getRemainTime(props.row))} นาที`}
        </div>
      }
      return <>{props.row.totalTime ? `${getMinuteTimeV2(props.row.startedTime, props.row.finishedTime)} นาที` : '-'}</>
    }
  },
  { key: 'RT', name: 'RT', headerCellClass: ' text-center', cellClass: ' text-center', width: 130,
    formatter: (props: FormatterProps<DgtPdOrderOperationLog, SummaryLogRow>) => {
      if (props.row.timeType === OperationTime.DOWN_TIME) {
        return <>{props.row.totalTime! > 0 && props.row.totalEmployeeRT! > 0 ? `${props.row.totalEmployeeRT} คน/${(getRemainTime(props.row))*props.row.totalEmployeeRT!} นาที`: '-'}</>
      }
      return <>{props.row.totalTime! >= 0 && props.row.totalEmployeeRT! > 0 ? `${props.row.totalEmployeeRT} คน/${getMinuteTimeV2(props.row.startedTime, props.row.finishedTime) * props.row.totalEmployeeRT!} นาที`: '-'}</>
    }
  },
  { key: 'OT', name: 'OT', headerCellClass: ' text-center', cellClass: ' text-center', width: 130,
    formatter: (props: FormatterProps<DgtPdOrderOperationLog, SummaryLogRow>) => {
      if (props.row.timeType === OperationTime.DOWN_TIME) {
        return <>{props.row.totalTime! > 0 && props.row.totalEmployeeOT! > 0 ? `${props.row.totalEmployeeOT} คน/${(getRemainTime(props.row))*props.row.totalEmployeeOT!} นาที`: '-'}</>
      }
      return <>{props.row.totalTime! >= 0 && props.row.totalEmployeeOT! > 0 ? `${props.row.totalEmployeeOT} คน/${getMinuteTimeV2(props.row.startedTime, props.row.finishedTime)*props.row.totalEmployeeOT!} นาที`: '-'}</>
    },
    summaryFormatter() {
      return <span className='float-right font-bold'>ผลิตได้รวม</span>
    }
  },
  { key: 'yield', name: 'ผลิตได้', headerCellClass: ' text-center', cellClass: ' text-center', width: 90, summaryCellClass: ' text-center',
    formatter: (props: FormatterProps<DgtPdOrderOperationLog, SummaryLogRow>) => {
      if (props.row.timeType === OperationTime.SOAP_BASE) {
        return <>{formatNumberV2(props.row.bulkUsedQty)}</>
      }
      return <>{formatNumberV2(props.row.yield)}</>
    },
    summaryFormatter({ row }) {
      return <span className='font-bold text-green-600'>{formatNumberV2(row.totalYield)}</span>
    }
  },
  { key: 'scrap', name: 'ของเสีย', headerCellClass: ' text-center', cellClass: ' text-center', width: 70, minWidth: 70,
    formatter: (props: FormatterProps<DgtPdOrderOperationLog, SummaryLogRow>) => {
      return <>{formatNumberDigit(props.row.scrap, 3)}</>
    }
  },
  { key: 'bulkUsedQty', name: 'ใช้Bulk', headerCellClass: ' text-center', cellClass: ' text-center', width: 100,
    formatter: (props: FormatterProps<DgtPdOrderOperationLog, SummaryLogRow>) => {
      return <>{formatNumberV2(props.row.bulkUsedQty)}</>
    }
  },
  { key: 'createdBy', name: 'บันทึกโดย', headerCellClass: ' text-center', cellClass: ' text-center' },
  { key: 'confirmedBy', name: 'ยืนยันโดย', headerCellClass: ' text-center', cellClass: ' text-center', width: 120 },
  { key: 'confirmedDate', name: 'เวลายืนยัน', headerCellClass: ' text-center', cellClass: ' text-center relative cursor-pointer group', width: 150,
    formatter: (props: FormatterProps<DgtPdOrderOperationLog, SummaryLogRow>) => {
      return props.row.confirmationId ? <>
        <span className='absolute left-0 right-0 group-hover:opacity-20' >{formatDateISO(props.row.confirmedDate)}</span>
        <div className='flex items-center justify-center h-full text-red-500 opacity-0 group-hover:opacity-100'>
          <AiOutlineClose size={20} /> ยกเลิก
        </div>
      </> : <></>
    },
    editable : row => row.confirmationId ? true : false,
    editor : (props: EditorProps<DgtPdOrderOperationLog, SummaryLogRow>) => {
      return <CancelConfirmEditor props={props} />
    }, editorOptions: {
      editOnClick: true
    }
  },
  { key: 'edit', name: 'แก้ไข', headerCellClass: ' text-center', cellClass: ' text-center', width: 60, minWidth: 60,
    formatter: (props: FormatterProps<DgtPdOrderOperationLog, SummaryLogRow>) => {
      if (props.row.timeType === OperationTimeQC.INSPECT_BULK) return <>-</>
      return <div className='work-center-edit' onClick={() => props.onRowChange(props.row)}>แก้ไข</div>
    }
  },
  { key: 'delete', name: 'ลบ', headerCellClass: ' text-center', width: 60, minWidth: 60, cellClass: ' text-center',
    formatter: (props: FormatterProps<DgtPdOrderOperationLog, SummaryLogRow>) => {
      if (props.row.timeType === OperationTimeQC.INSPECT_BULK) return <>-</>
      return <button className="px-2 py-1 text-xs text-white bg-red-500 rounded">ลบ</button>
    }
  }
]

export const UserColumns: readonly Column<DgtPdIAMUser>[] = [
  { key: 'username', name: 'Username', width: 260, formatter: (props: FormatterProps<DgtPdIAMUser>) => {
    const isSSO = props.row.username.startsWith('digital-production-')
    return <div className='flex items-center gap-1'>{props.row.isAdmin ?
      <MdSupervisedUserCircle size={20} className='text-red-500'/>: ''}{isSSO ? <SiMicrosoftoutlook size={20} className='text-blue-500 '/> :''}{isSSO ? props.row.username.split('_')[1] : props.row.username}</div>
  } },
  { key: 'employeeNo', name: 'Employee No.', width: 120 },
  { key: 'firstName', name: 'First name', width: 130 },
  { key: 'lastName', name: 'Last name', width: 150 },
  { key: 'division', name: 'Division', width: 90 },
  { key: 'line', name: 'Line', width: 80 },
  { key: 'roles', name: 'Roles', formatter: (props: FormatterProps<DgtPdIAMUser>) => {
    const roles : { name: string, readOnly: boolean }[] = props.row.roles ? JSON.parse(props.row.roles) : []
    return <div className='flex gap-1'>
      {roles.map(role => <p key={role.name}><Badge variant={` text-xs text-white ${role.readOnly ? ' bg-gray-400' : ' bg-green-600'}`}><>{role.name} {role.readOnly ? '- ReadOnly' : ''}</></Badge></p>)}
    </div>
  }},
  { key: 'active', name: 'Active', minWidth: 60, width: 60, cellClass: ' flex items-center justify-center',
    formatter: (props: FormatterProps<DgtPdIAMUser>) => {
      return props.row.active ? <AiFillCheckCircle size={20} className='text-green-600 ' /> : <AiFillCloseCircle size={20} className='text-red-600 ' />
    }
  },
  { key: 'edit', name: 'Edit', minWidth: 50, width: 50, cellClass: ' flex items-center justify-center cursor-pointer',
    formatter: (props: FormatterProps<DgtPdIAMUser>) => {
      return <MdModeEdit size={20} className='text-blue-500 ' />
    },
    editor(props: EditorProps<DgtPdIAMUser>) {
      return <UserEditor props={props} />
    },
    editable: true,
    editorOptions: {
      editOnClick: true, commitOnOutsideClick: true
    }
  }
]

type Comparator = (a: DgtPdIAMUser, b: DgtPdIAMUser) => number;
export function getUserComparator(sortColumn: string): Comparator {
  switch (sortColumn) {
    case 'username':
    case 'employeeNo':
    case 'email':
    case 'firstName':
    case 'lastName':
    case 'department':
    case 'division':
    case 'line':
    case 'roles':
      return (a, b) => {
        return (a[sortColumn] || '').localeCompare(b[sortColumn] || '');
      };
    case 'active':
      return (a, b) => {
        return a[sortColumn] === b[sortColumn] ? 0 : a[sortColumn] ? 1 : -1;
      };
    default:
      throw new Error(`unsupported sortColumn: "${sortColumn}"`);
  }
}

const getRemainTime = (log: DgtPdOrderOperationLog) => {
  let remainTime = getMinuteTimeV2(log.startedTime, log.finishedTime) - (log.downtimeBaseTime ? log.downtimeBaseTime : 0)
  return remainTime < 0 ? 0 : remainTime
}

export const PicklistQaCheckOrderColumn: readonly Column<DgtPdOrderPicklist>[] = [
  { key: 'checkedBy', name: 'PD ตรวจโดย', headerCellClass: ' text-center', cellClass: ' text-center',
    formatter: ({ row }: FormatterProps<DgtPdOrderPicklist>) => {
      if (row.checkedDateTime) return <div className='p-1 text-sm'>
        <div className='flex items-center justify-center gap-1 '>
          <AiOutlineCheckCircle color='green' size={20}/>{row.checkedBy}
        </div>
        <span className='text-xs text-muted'>({formatDateFromUnix(row.checkedDateTime)})</span>
      </div>
      return <>-</>
    },
  },
  { key: 'qualityCheckedBy', name: 'PD ตรวจ RM โดย', headerCellClass: ' text-center', cellClass: ' text-center',
    formatter: ({ row }: FormatterProps<DgtPdOrderPicklist>) => {
      if (row.qualityCheckedDateTime) return <div className='p-1 text-sm'>
        <div className='flex items-center justify-center gap-1 '>
          <AiOutlineCheckCircle color='green' size={20}/>{row.qualityCheckedBy}
        </div>
        <span className='text-xs text-muted'>({formatDateFromUnix(row.qualityCheckedDateTime)})</span>
      </div>
      return <>-</>
    },
  },
  { key: 'commentText', name: 'หมายเหตุ' }
]

export const PicklistItemQaCheckMvColumn: readonly Column<DgtPdOrderPicklistMovement>[] = [
  { key: 'materialCode', name: 'รหัสสินค้า', width: 80, groupFormatter({ childRows, isExpanded, column }) {
    return <div className='flex items-center'>
      <span>{childRows[0].materialCode}</span>
      {isExpanded ? <MdKeyboardArrowUp size={20}/> : <MdKeyboardArrowDown size={20}/>}
    </div>
  }},
  { key: 'materialDescription', name: 'ชื่อ', width: 150, frozen: true ,formatter: (props: FormatterProps<DgtPdOrderPicklistMovement>) => {
    return <></>
  }, groupFormatter({ childRows }) {
    return <div>{childRows[0].materialDescription}</div>
  } },
  { key: 'requestQty', name: 'เบิก', width: 80, formatter: (props: FormatterProps<DgtPdOrderPicklistMovement>) => {
    return <></>
  }, groupFormatter({ childRows }) {
    return <div className='text-right '>{formatNumber(childRows[0].materialRequestQty)}</div>
  } },
  { key: 'amount', name: 'จ่าย', width: 80, formatter: (props: FormatterProps<DgtPdOrderPicklistMovement>) => {
    return <div className='text-right '>{formatNumber(props.row.amount)}</div>
  }, groupFormatter({ childRows }) {
    let total = childRows.reduce((prev, { amount }) => prev + amount, 0)
    if (!Number.isInteger(total)) total = Number(total.toFixed(3))
    const diff = total - (childRows[0].materialRequestQty || 0)
    return <div className={` text-right${diff === 0 ? ' text-green-600': (diff > 0 ? ' text-blue-700':' text-red-400')} font-bold`}>{formatNumber(total)}</div>;
  } },
  { key: 'materialUnit', name: 'Unit', width: 45, minWidth: 45, groupFormatter({ childRows }) {
    return <>{childRows[0].materialUnit}</>
  } },
  { key: 'batchLotNo', name: 'Batch No', width: 105 },
  { key: 'qualityCheckedDateTime', name: 'QC เมื่อ', width: 150, formatter: (props: FormatterProps<DgtPdOrderPicklistMovement>) => {
    if (props.row.qualityCheckedDateTime)
      return <div className='flex items-center justify-center gap-1 '>
        <BiCheckDouble color='green' size={20}/>{formatDateFromUnix(props.row.qualityCheckedDateTime)}
      </div>
    return <div className='text-center '>-</div>
  }},
  {
    key: 'isChecked', name: 'ลงชื่อ', 
      formatter: ({ row }: FormatterProps<DgtPdOrderPicklistMovement>) => {
      return <div className='flex items-center justify-center h-full'>
        <h6 className={`work-center-edit ${row.qualityCheckedDateTime ? 'text-red-600':'text-green-600'}`}>{row.qualityCheckedDateTime ? 'ยกเลิก':'ผ่าน ?'}</h6>
      </div>
    }, editor(props: EditorProps<DgtPdOrderPicklistMovement>) {
      return <QaCheckConfirmEditor props={props} isChecked={props.row.qualityCheckedDateTime! > 0}/>
    }, editorOptions: {
      editOnClick: true, commitOnOutsideClick: true
    }
  }
]

export const WasteReturnMvColumn: readonly Column<DgtPdOrderPicklistMovement>[] = [
  { key: 'orderId', name: 'PD ORDER#', width: 100, cellClass: ' work-center-edit' },
  { key: 'materialCode', name: 'Material Code', width: 120 },
  { key: 'materialDescription', name: 'Description' },
  { key: 'batchLotNo', name: 'Batch Lot No', width: 120 },
  { key: 'returnWasteQty', name: 'Qty', width: 80, formatter: (props: FormatterProps<DgtPdOrderPicklistMovement>) => {
    return <div className='text-right '>{formatNumberV2(props.row.returnWasteQty)}</div>
  }  },
  { key: 'materialUnit', name: 'Unit', minWidth: 50, width: 50 },
  { key: 'recordedDateTime', name: 'บันทึกเมื่อ', width: 130,
    cellClass: "multi-line-row-data-grid",
    formatter: ({ row }: FormatterProps<DgtPdOrderPicklistMovement>) => {
      return <div className='flex flex-col items-center justify-center gap-1'>
        <span>{row.recordedBy}</span>
        <span>{formatDateFromUnix(row.recordedDateTime)}</span>
      </div>
    }
  },
  { key: 'approvedWasteReturnDateTime', name: 'ตรวจสอบเมื่อ', width: 130,
    cellClass: "multi-line-row-data-grid",
    formatter: ({ row }: FormatterProps<DgtPdOrderPicklistMovement>) => {
      return <div className='flex flex-col items-center justify-center gap-1'>
        <span className='font-bold text-green-700'>{row.approvedWasteReturnBy}</span>
        <span>{formatDateFromUnix(row.approvedWasteReturnDateTime)}</span>
      </div>
    }
  },
  { key: 'status', name: 'Status', width: 100, 
    cellClass: 'flex items-center justify-center h-full',
    formatter: ({ row }: FormatterProps<DgtPdOrderPicklistMovement>) => {
      if ((row.wasteRecordedStatus === WasteRecordStatus.PD_CONFIRMED) || (row.wasteRecordedStatus === WasteRecordStatus.QC_APPROVED)) {
        if (row.approvedWasteReturnDateTime! > 0 || row.wasteRecordedStatus === WasteRecordStatus.QC_APPROVED) {
          return <Badge variant={` bg-green-600 text-xs text-white`}>☑ อนุมัติแล้ว</Badge> 
        } else {
          return <Badge variant={` bg-amber-400 text-xs text-white`}>○ รออนุมัติ</Badge>
        }
      }else if (row.wasteRecordedStatus === WasteRecordStatus.PD_PENDING) {
        return <Badge variant={` bg-stone-400 text-xs text-white`}>◴ รอ PD ยืนยัน</Badge>
      } else {
        return <Badge variant={` bg-stone-400 text-xs text-white`}>-</Badge>
      }
    }
  },
  { key: 'approve', name: 'Approve', 
    width: 80,
    cellClass: 'flex items-center justify-center h-full',
    formatter: ({ row }: FormatterProps<DgtPdOrderPicklistMovement>) => {
      if (row.approvedWasteReturnDateTime! > 0) return <>-</>
      if (row.wasteRecordedStatus === WasteRecordStatus.PD_CONFIRMED) {
        return <h6 className={`flex items-center gap-1 work-center-edit text-green-600`}><AiOutlineCheck /> อนุมัติ</h6>
      } else {
        return <>-</>
      }
    },
    editor(props: EditorProps<DgtPdOrderPicklistMovement>) {
      if (props.row.wasteRecordedStatus === WasteRecordStatus.PD_CONFIRMED) {
        return <WasteConfirmEditor props={props} isApprove={true} />
      } else {
        return <>-</>
      }
    }, editorOptions: {
      editOnClick: true, commitOnOutsideClick: true
    }
  },
  { key: 'reject', name: 'Reject',
    width: 80,
    cellClass: 'flex items-center justify-center h-full',
    formatter: ({ row }: FormatterProps<DgtPdOrderPicklistMovement>) => {
      if (row.approvedWasteReturnDateTime! > 0) return <>-</>
      if (row.wasteRecordedStatus === WasteRecordStatus.PD_CONFIRMED) {
        return <h6 className={`flex items-center gap-1 work-center-edit text-red-600`}><AiOutlineClose /> ปฎิเสธ</h6>
      } else {
        return <>-</>
      }
    }, 
    editor(props: EditorProps<DgtPdOrderPicklistMovement>) {
      if (props.row.wasteRecordedStatus === WasteRecordStatus.PD_CONFIRMED) {
        return <WasteConfirmEditor props={props} isApprove={false} />
      } else {
        return <>-</>
      }
    }, editorOptions: {
      editOnClick: true, commitOnOutsideClick: true
    }
  },
]

export const PicklistItemReportColumn: readonly Column<OrderPicklistMovementReport>[] = [
  { key: 'materialCode', name: 'รหัส Mat', width: 130, groupFormatter({ childRows, isExpanded }) {
    return <div className='flex items-center'>
      <span>{childRows[0].materialCode}</span>
      {isExpanded ? <MdKeyboardArrowUp size={18}/> : <MdKeyboardArrowDown size={18}/>}
    </div>
  } },
  { key: 'materialDescription', name: 'ชื่อ Mat', frozen: true ,
    formatter: (props: FormatterProps<OrderPicklistMovementReport>) => {
      return <></>
    }, groupFormatter({ childRows }) {
      return <div className='leading-[normal] flex flex-col h-[100%] justify-center gap-1' >
        <p>{childRows[0].materialDescription}</p>
        {childRows[0].barcode && <AmplifyBadge size='small' variation="success" className='amplify-badge-w-fit'><RiBarcodeLine />{childRows[0].barcode}</AmplifyBadge>}
      </div>
    }
  },
  { key: 'requestQty', name: 'เบิก', width: 80, frozen: true,
    formatter: (props: FormatterProps<OrderPicklistMovementReport>) => {
      return <></>
    }, groupFormatter({ childRows }) {
      const uniqueQtys = new Set(childRows.map(row => (row.materialRequestQty || 0)))
      const sumQty = Array.from(uniqueQtys).reduce((prev, qty) => prev + qty, 0)
      return childRows[0].materialType === 'BULK' ? 
        <div className='font-bold '>{formatNumberV2(childRows.reduce((prev, { materialRequestQty }) => prev + (materialRequestQty || 0), 0))}</div>
        : <div className='font-bold '>{formatNumberV2(sumQty)}</div>
    }
  },
  { key: 'withdrawQty', name: 'จ่าย', width: 80, frozen: true,
    formatter: (props: FormatterProps<OrderPicklistMovementReport>) => {
      return <></>
    }, groupFormatter({ childRows }) {
      return <div className='font-bold '>{formatNumberV2(childRows.reduce((prev, { amount }) => prev + amount, 0))}</div>
    }
  },
  { key: 'materialUnit', name: 'Unit', width: 45, minWidth: 45,
    formatter: (props: FormatterProps<OrderPicklistMovementReport>) => {
      return <></>
    },
    groupFormatter({ childRows }) {
      return <div className='font-bold '>{childRows[0].materialUnit}</div>
    }
  },
  { key: 'documentCreatedAt', name: 'เวลาจ่าย', width: 150, 
    formatter: (props: FormatterProps<OrderPicklistMovementReport>) => {
      return <div>{formatDateFromUnix(props.row.materialType === 'BULK' ? props.row.approvedDateTime: props.row.documentCreatedAt)}</div>
    }, groupFormatter({ childRows }) {
      return <div className='font-bold '>{childRows.length > 1 ? '' : formatDateFromUnix(
        childRows[0].materialType === 'BULK' ? childRows[0].approvedDateTime: childRows[0].documentCreatedAt
      )}</div>
    }
  },
  { key: 'usedGoodQty', name: 'ใช้ดี', width: 80,
    formatter: (props: FormatterProps<OrderPicklistMovementReport>) => {
      return <div className='text-right ' >{formatNumberV2(props.row.usedGoodQty)}</div>
    }, groupFormatter({ childRows }) {
      return <div className='font-bold text-right text-green-500'>{
        formatNumberV2(childRows.reduce((prev, { usedGoodQty }) => prev + usedGoodQty!, 0))
      }</div>
    }
  },
  { key: 'usedWasteQty', name: 'ใช้เสีย', width: 80,
    formatter: (props: FormatterProps<OrderPicklistMovementReport>) => {
      return <div className='text-right ' >{formatNumberV2(props.row.usedWasteQty)}</div>
    }, groupFormatter({ childRows }) {
      return <div className='font-bold text-right text-red-500'>{
        formatNumberV2(childRows.reduce((prev, { usedWasteQty }) => prev + usedWasteQty!, 0))
      }</div>
    }
  },
  { key: 'returnGoodQty', name: 'คืนดี', width: 80, 
    formatter: (props: FormatterProps<OrderPicklistMovementReport>) => {
      return <div className='text-right ' >{formatNumberV2(props.row.returnGoodQty)}</div>
    }, groupFormatter({ childRows }) {
      return <div className='font-bold text-right text-gray-500'>{
        formatNumberV2(childRows.reduce((prev, { returnGoodQty }) => prev + returnGoodQty!, 0))
      }</div>
    }
  },
  { key: 'returnGoodShrinkQty', name: 'คืนดีติดSH/STK', width: 120,
    formatter: (props: FormatterProps<OrderPicklistMovementReport>) => {
      return <div className='text-right ' >{formatNumberV2(props.row.returnGoodShrinkQty)}</div>
    }, groupFormatter({ childRows }) {
      return <div className='font-bold text-right text-gray-500'>{
        formatNumberV2(childRows.reduce((prev, { returnGoodShrinkQty }) => prev + returnGoodShrinkQty!, 0))
      }</div>
    }
  },
  { key: 'returnWasteQty', name: 'คืนเสีย', width: 80,
    formatter: (props: FormatterProps<OrderPicklistMovementReport>) => {
      return <div className='text-right ' >{formatNumberV2(props.row.returnWasteQty)}</div>
    }, groupFormatter({ childRows }) {
      return <div className='font-bold text-right text-gray-500'>{
        formatNumberV2(childRows.reduce((prev, { returnWasteQty }) => prev + returnWasteQty!, 0))
      }</div>
    }
  },
  { key: 'batchLotNo', name: 'Batch No', width: 120,
    groupFormatter({ childRows }) {
      return <div className='font-bold'>{childRows.length === 1 ? childRows[0].batchLotNo : '' }</div>
    }
  },
  { key: 'extraInfo', name: 'หมายเหตุ', width: 80,
    formatter: (props: FormatterProps<OrderPicklistMovementReport>) => {
      return props.row.isExtra ? <Badge variant='warning'>เบิกเพิ่ม</Badge> : <></>
    }
  }
]

export const BulkInspectionLogColumn: readonly Column<DgtPdOrderOperationLog>[] = [
  { key: 'productionOrderId', name: 'PD ORDER#', width: 100, groupFormatter({ childRows, isExpanded, column }) {
    return <div className='flex items-center'>
      <span>{childRows[0].productionOrderId}</span>
      {isExpanded ? <MdKeyboardArrowUp size={20}/> : <MdKeyboardArrowDown size={20}/>}
    </div>
  } },
  { key: 'materialCode', name: 'Material Code', width: 120,
    formatter: (props: FormatterProps<DgtPdOrderOperationLog>) => {
      return <></>
    },
    groupFormatter({ childRows }) {
      return <div>{childRows[0].materialCode}</div>
    }
  },
  { key: 'materialDescription', name: 'Description', resizable: true,
    formatter: (props: FormatterProps<DgtPdOrderOperationLog>) => {
      return <></>
    },
    groupFormatter({ childRows }) {
      return <div>{childRows[0].materialDescription}</div>
    } 
  },
  { key: 'batchNo', name: 'Batch Lot No', width: 120,
    formatter: (props: FormatterProps<DgtPdOrderOperationLog>) => {
      return <></>
    },
    groupFormatter({ childRows }) {
      return <div>{childRows[0].batchNo || ''}</div>
    }
  },
  { key: 'startedTime', name: 'เริ่มตรวจเมื่อ', width: 130,
    cellClass: "multi-line-row-data-grid",
    formatter: ({ row }: FormatterProps<DgtPdOrderOperationLog>) => {
      return <div className='flex flex-col items-center justify-center gap-1'>
        <span>{row.createdBy}</span>
        <span>{formatDateFromUnix(row.startedTime)}</span>
      </div>
    }
  },
  { key: 'finishedTime', name: 'ตรวจเสร็จเมื่อ', width: 130,
    cellClass: "multi-line-row-data-grid",
    formatter: ({ row }: FormatterProps<DgtPdOrderOperationLog>) => {
      return <div className='flex flex-col items-center justify-center gap-1'>
        <span>{row.bulkStatusBy}</span>
        <span>{formatDateFromUnix(row.finishedTime)}</span>
      </div>
    }
  },
  { key: 'totalTime', name: 'เวลาที่ใช้', width: 90,
    formatter: ({ row }: FormatterProps<DgtPdOrderOperationLog>) => {
      return <div className='flex flex-col items-center justify-center gap-1'>
        <span>{row.totalTime ? `${getMinuteTimeV2(row.startedTime, row.finishedTime)} นาที` : '-'}</span>
        </div>
    }
  },
  { key: 'bulkStatus', name: 'Bulk Status', width: 100, 
    cellClass: 'flex items-center justify-center h-full',
    formatter: ({ row }: FormatterProps<DgtPdOrderOperationLog>) => {
      if (row.bulkStatus === BulkStatus.PASS) {
        return <Badge variant={` bg-green-600 text-xs text-white`}>{row.bulkStatus}</Badge> 
      } else if (row.bulkStatus === BulkStatus.HOLD) {
        return <Badge variant={` bg-amber-400 text-xs text-white`}>{row.bulkStatus}</Badge>
      } else if (row.bulkStatus === BulkStatus.REJECT) {
        return <Badge variant={` bg-rose-600 text-xs text-white`}>{row.bulkStatus}</Badge> 
      } else {
        return <></>
      }
    }
  },
  { key: 'bulkStatusDateTime', name: 'บันทึกเมื่อ', width: 130,
    cellClass: "multi-line-row-data-grid",
    formatter: ({ row }: FormatterProps<DgtPdOrderOperationLog>) => {
      return <div className='flex flex-col items-center justify-center gap-1'>
          <span>{row.bulkStatusBy}</span>
          <span>{formatDateFromUnix(row.bulkStatusDateTime)}</span>
        </div>
      }
  },
  { key: 'bulkStatusComment', name: 'Bulk Comment', resizable: true},
  { key: 'approveBulkStatusDateTime', name: 'อนุมัติเมื่อ', width: 130,
    cellClass: "multi-line-row-data-grid",
    formatter: ({ row }: FormatterProps<DgtPdOrderOperationLog>) => {
      return <div className='flex flex-col items-center justify-center gap-1'>
        <span className='font-bold text-green-700'>{row.approveBulkStatusBy}</span>
        <span>{formatDateFromUnix(row.approveBulkStatusDateTime)}</span>
      </div>
    }
  },
  { key: 'status', name: 'สถานะการอนุมัติ', width: 115, 
    cellClass: 'flex items-center justify-center h-full',
    formatter: ({ row }: FormatterProps<DgtPdOrderOperationLog>) => {
      if (row.bulkStatus === BulkStatus.HOLD) return <>-</>
      if (row.approveBulkStatusDateTime! > 0) {
        return <Badge variant={` bg-green-600 text-xs text-white`}>☑ อนุมัติแล้ว</Badge> 
      } else {
        return <Badge variant={` bg-amber-400 text-xs text-white`}>○ รออนุมัติ</Badge>
      }
    }
  },
  { key: 'approve', name: 'Approve', 
    width: 80,
    cellClass: 'flex items-center justify-center h-full',
    editable: (row) => {
      if (row.approveBulkStatusDateTime! > 0) return false
      return true;
    },
    formatter: ({ row }: FormatterProps<DgtPdOrderOperationLog>) => {
      if (row.bulkStatus === BulkStatus.HOLD) return <>-</>
      if (row.approveBulkStatusDateTime! > 0) return <>-</>
      return <h6 className={`flex items-center gap-1 work-center-edit text-green-600`}><AiOutlineCheck /> อนุมัติ</h6>
    },
    editor(props: EditorProps<DgtPdOrderOperationLog>) {
      return <BulkOperationLogEditor props={props} isApprove={true} />
    }, editorOptions: {
      editOnClick: true, commitOnOutsideClick: true
    }
  }
]

export const InspectBulkLogsColumns: readonly Column<DgtPdOrderOperationLog>[] = [
  { key: 'batchNo', name: 'Batch No', width: 110, headerCellClass: ' text-center', cellClass: ' text-center' },
  { key: 'postingDate', name: 'Posting Date', headerCellClass: ' text-center', cellClass: ' text-center', width: 120, 
    formatter: (props: FormatterProps<DgtPdOrderOperationLog>) => {
      return <>{format(new Date(props.row.postingDate), 'dd-MM-yyyy')}</>
    }
  },
  { key: 'bulkStatus', name: 'Bulk Status', width: 100,
    headerCellClass: ' text-center',
    cellClass: 'flex items-center justify-center h-full',
    formatter: ({ row }: FormatterProps<DgtPdOrderOperationLog>) => {
      if (row.bulkStatus === BulkStatus.PASS) {
        return <Badge variant={` bg-green-600 text-xs text-white`}>{row.bulkStatus}</Badge> 
      } else if (row.bulkStatus === BulkStatus.HOLD) {
        return <Badge variant={` bg-amber-400 text-xs text-white`}>{row.bulkStatus}</Badge>
      } else if (row.bulkStatus === BulkStatus.REJECT) {
        return <Badge variant={` bg-rose-600 text-xs text-white`}>{row.bulkStatus}</Badge> 
      } else {
        return <></>
      }
    }
  },
  { key: 'bulkStatusBy', name: 'บันทึกโดย', headerCellClass: ' text-center', cellClass: ' text-center' },
  { key: 'bulkStatusDateTime', name: 'บันทึกเมื่อ', width: 150, headerCellClass: ' text-center', formatter: (props: FormatterProps<DgtPdOrderOperationLog>) => {
    return <div className='text-center '>{formatDateFromUnix(props.row.bulkStatusDateTime)}</div>
  }},
  { key: 'bulkStatusComment', name: 'Bulk Comment', resizable: true, headerCellClass: ' text-center',},
  { key: 'approveBulkStatusBy', name: 'อนุมัติโดย', headerCellClass: ' text-center', cellClass: ' text-center' },
  { key: 'approveBulkStatusDateTime', name: 'อนุมัติเมื่อ', width: 150, headerCellClass: ' text-center', formatter: (props: FormatterProps<DgtPdOrderOperationLog>) => {
    return <div className='text-center '>{formatDateFromUnix(props.row.bulkStatusDateTime)}</div>
  }},
  { key: 'edit', name: 'แก้ไข', headerCellClass: ' text-center', cellClass: ' text-center', width: 60, minWidth: 60,
    formatter: (props: FormatterProps<DgtPdOrderOperationLog>) => {
      if (props.row.approveBulkStatusDateTime! > 0) return <>-</>
      return <div className='work-center-edit' >แก้ไข</div>
    }
  },
  { key: 'delete', name: 'ลบ', headerCellClass: ' text-center', width: 60, minWidth: 60, cellClass: ' text-center',
    formatter: (props: FormatterProps<DgtPdOrderOperationLog>) => {
      if (props.row.approveBulkStatusDateTime! > 0) return <>-</>
      return <button className="px-2 py-1 text-xs text-white bg-red-500 rounded">ลบ</button>
    }
  }
]

export const OrderReceiptColumns: readonly Column<OrderReceipt, SummaryReceiptRow>[] = [
  { key: 'batchLotNo', name: 'Batch Lot', width: 120, frozen: true,
    colSpan(args) {
      if (args.type === 'SUMMARY') return 2
      return undefined
    },
    summaryFormatter() {
      return <strong className='float-right '>ยอดรวม</strong>
    }
  },
  { key: 'avgFillingWeight', name: 'Avg. Fwt', cellClass: ' text-right',
    width: 80,
    headerCellClass: ' flex flex-col leading-[normal] items-end justify-center',
    headerRenderer: (props: HeaderRendererProps<OrderReceipt, SummaryReceiptRow>) => {
      return <>
        <span>Avg. Fwt</span>
        <span className='text-[0.5rem] text-muted '>G</span>
      </>
    },
    formatter: (props: FormatterProps<OrderReceipt, SummaryReceiptRow>) => {
      return <>{formatNumberDigit(props.row.avgFillingWeight, 2)}</>
    },
    frozen: true
  },
  { key: 'bulkUsage', name: 'Bulk',
    cellClass: ' text-right',
    width: 120,
    headerCellClass: ' flex flex-col leading-[normal] items-end justify-center',
    headerRenderer: (props: HeaderRendererProps<OrderReceipt, SummaryReceiptRow>) => {
      return <>
        <span>Bulk</span>
        <span className='text-[0.5rem] text-muted '>KG</span>
      </>
    },
    summaryCellClass: ' flex justify-end items-center w-full text-xl',
    formatter: (props: FormatterProps<OrderReceipt, SummaryReceiptRow>) => {
      if (!props.column.editable) return <>{formatNumberV2(props.row.bulkUsage)}</>

      if (!props.row.bulkUsage || props.row.bulkUsage === 0)
        return <div className='flex items-center justify-end h-full'><AiFillEdit className='text-[#007bff]' /></div>
      return <div className='flex items-center justify-between'>
        <AiFillEdit className=' text-[#007bff]' />
        <span className={` font-bold`}>{formatNumberV2(props.row.bulkUsage)}</span>
      </div>
    },
    frozen: true,
    summaryFormatter({ row }) {
      return <div className="font-bold text-right after:content-['KG'] after:ml-1 after:text-muted after:text-xs">{formatNumberV2(row.bulkUsed)}</div>
    },
    editable: false,
    editor(props: EditorProps<OrderReceipt, SummaryReceiptRow>) {
      return <NumberEditor onSubmit={(num) => props.onRowChange({...props.row, bulkUsage: num}, true)} />
    },
    editorOptions: { editOnClick: true, commitOnOutsideClick: true },
  },
  { key: 'totalAmount', name: 'EA', 
    cellClass: ' text-right',
    width: 120,
    headerCellClass: ' flex flex-col leading-[normal] items-end justify-center',
    headerRenderer: (props: HeaderRendererProps<OrderReceipt, SummaryReceiptRow>) => {
      return <>
        <span>ผลิตได้</span>
        <span className='text-[0.5rem] text-muted '>EA</span>
      </>
    },
    summaryCellClass: ' flex justify-end items-center w-full text-xl',
    formatter: (props: FormatterProps<OrderReceipt, SummaryReceiptRow>) => {
      if (!props.column.editable) return <>{formatNumberV2(props.row.totalAmount)}</>

      if (!props.row.totalAmount || props.row.totalAmount === 0)
        return <div className='flex items-center justify-end h-full'><AiFillEdit className='text-[#007bff]' /></div>
      return <div className='flex items-center justify-between'>
        <AiFillEdit className=' text-[#007bff]' />
        <span className={` font-bold`}>{formatNumberV2(props.row.totalAmount)}</span>
      </div>
    },
    frozen: true,
    summaryFormatter({ row }) {
      return <div className="font-bold text-right after:content-['EA'] after:ml-1 after:text-muted after:text-xs">{formatNumberV2(row.ea)}</div>
    },
    editable: true,
    editor(props: EditorProps<OrderReceipt, SummaryReceiptRow>) {
      return <NumberEditor onSubmit={(num) => props.onRowChange({...props.row, totalAmount: num}, true)} />
    },
    editorOptions: { editOnClick: true, commitOnOutsideClick: true },
  },
  {
    key: 'yieldTolerance', name: '% Yield',
    width: 80,
    headerCellClass: ' flex flex-col leading-[normal] items-end justify-center',
    headerRenderer: (props: HeaderRendererProps<OrderReceipt, SummaryReceiptRow>) => {
      return <>
        <span>% Yield</span>
        <span className='text-[0.5rem] text-muted '>กดเลขเพื่อดูคำนวณ</span>
      </>
    },
    cellClass: (row) => {
      const yieldTolerance = row.yieldTolerance || 0
      // Tolerance % yield FG : 95.00% - 105.00%
      if (yieldTolerance === 0 || !row.isFilling) return ''
      
      const estimateYield = Number(formatNumberDigit(yieldTolerance, 2))
      if (estimateYield >= 95 && estimateYield <= 105) return 'text-right font-bold bg-green-600'
      
      return 'text-right font-bold bg-red-500'
    },
    formatter: (props: FormatterProps<OrderReceipt, SummaryReceiptRow>) => {
      const { row } = props

      const yieldTolerance = row.yieldTolerance || 0
      
      return yieldTolerance === 0 || !row.isFilling ? <>-</> : <>{formatNumberDigit(yieldTolerance, 2)}</>
    },
    summaryCellClass: row => {
      const y = row.yield || 0
      if (y === 0) return ''

      return y >= 95 && y <= 105 ? 'flex justify-end items-center w-full text-xl bg-green-600' : 'flex justify-end items-center w-full text-xl bg-red-500'
    },
    summaryFormatter({ row }) {
      if (row.yield === 0) return <></>
      return <div className='font-bold text-right'>{formatNumberDigit(row.yield, 2)}</div>
    },
    frozen: true,
    editable(row) {
      return row.isFilling || false
    },
    editor(props: EditorProps<OrderReceipt, SummaryReceiptRow>) {
      return <ShowInfoEditor props={props} title='รายละเอียดการคำนวณ Yield' />
    },
    editorOptions: { editOnClick: true, commitOnOutsideClick: true },
  },
  { key: 'retainedIssue', name: 'Retained', 
    minWidth: 55, width: 55,
    cellClass: ' text-right text-amber-600',
    headerCellClass: ' flex flex-col leading-[normal] items-end justify-center text-amber-600',
    headerRenderer: (props: HeaderRendererProps<OrderReceipt, SummaryReceiptRow>) => {
      return <>
        <span>R</span>
        <span className='text-[0.5rem] text-gray-700'>EA</span>
      </>
    },
    formatter: (props: FormatterProps<OrderReceipt, SummaryReceiptRow>) => {
      if (!props.column.editable) return <>{formatNumberV2(props.row.retainedIssue)}</>

      if (!props.row.retainedIssue || props.row.retainedIssue === 0)
        return <div className='flex items-center justify-end h-full'><AiFillEdit className='text-[#007bff]' /></div>
      return <div className='flex items-center justify-between'>
        <AiFillEdit className=' text-[#007bff]' />
        <span className={` font-bold`}>{formatNumberV2(props.row.retainedIssue)}</span>
      </div>
    },
    editable: true,
    editor(props: EditorProps<OrderReceipt, SummaryReceiptRow>) {
      return <NumberEditor onSubmit={(num) => props.onRowChange({...props.row, retainedIssue: num}, true)} />
    },
    editorOptions: { editOnClick: true, commitOnOutsideClick: true },
    summaryFormatter({ row }) {
      const totalQcSample = (row.retained || 0) + (row.micro || 0) + (row.validate || 0) + (row.shelfLife || 0) + (row.test || 0);
      return <div className="font-bold text-right text-amber-600">QC เบิก : {formatNumberV2(totalQcSample)} EA</div>
    },
    colSpan(args) {
      if (args.type === 'SUMMARY') return 5
      return undefined
    }
  },
  { key: 'microIssue', name: 'Micro', 
    minWidth: 55, width: 55,
    cellClass: ' text-right text-amber-600',
    headerCellClass: ' flex flex-col leading-[normal] items-end justify-center text-amber-600',
    headerRenderer: (props: HeaderRendererProps<OrderReceipt, SummaryReceiptRow>) => {
      return <>
        <span>M</span>
        <span className='text-[0.5rem] text-gray-700'>EA</span>
      </>
    },
    formatter: (props: FormatterProps<OrderReceipt, SummaryReceiptRow>) => {
      if (!props.column.editable) return <>{formatNumberV2(props.row.microIssue)}</>

      if (!props.row.microIssue || props.row.microIssue === 0)
        return <div className='flex items-center justify-end h-full'><AiFillEdit className='text-[#007bff]' /></div>
      return <div className='flex items-center justify-between'>
        <AiFillEdit className=' text-[#007bff]' />
        <span className={` font-bold`}>{formatNumberV2(props.row.microIssue)}</span>
      </div>
    },
    editable: true,
    editor(props: EditorProps<OrderReceipt, SummaryReceiptRow>) {
      return <NumberEditor onSubmit={(num) => props.onRowChange({...props.row, microIssue: num}, true)} />
    },
    editorOptions: { editOnClick: true, commitOnOutsideClick: true }
  },
  { key: 'validateIssue', name: 'Validate', 
    minWidth: 55, width: 55,
    cellClass: ' text-right text-amber-600',
    headerCellClass: ' flex flex-col leading-[normal] items-end justify-center text-amber-600',
    headerRenderer: (props: HeaderRendererProps<OrderReceipt, SummaryReceiptRow>) => {
      return <>
        <span>V</span>
        <span className='text-[0.5rem] text-gray-700'>EA</span>
      </>
    },
    formatter: (props: FormatterProps<OrderReceipt, SummaryReceiptRow>) => {
      if (!props.column.editable) return <>{formatNumberV2(props.row.validateIssue)}</>

      if (!props.row.validateIssue || props.row.validateIssue === 0)
        return <div className='flex items-center justify-end h-full'><AiFillEdit className='text-[#007bff]' /></div>
      return <div className='flex items-center justify-between'>
        <AiFillEdit className=' text-[#007bff]' />
        <span className={` font-bold`}>{formatNumberV2(props.row.validateIssue)}</span>
      </div>
    },
    editable: true,
    editor(props: EditorProps<OrderReceipt, SummaryReceiptRow>) {
      return <NumberEditor onSubmit={(num) => props.onRowChange({...props.row, validateIssue: num}, true)} />
    },
    editorOptions: { editOnClick: true, commitOnOutsideClick: true }
  },
  { key: 'shelfLifeIssue', name: 'Sh.Life', 
    minWidth: 55, width: 55,
    cellClass: ' text-right text-amber-600',
    headerCellClass: ' flex flex-col leading-[normal] items-end justify-center text-amber-600',
    headerRenderer: (props: HeaderRendererProps<OrderReceipt, SummaryReceiptRow>) => {
      return <>
        <span>S</span>
        <span className='text-[0.5rem] text-gray-700'>EA</span>
      </>
    },
    formatter: (props: FormatterProps<OrderReceipt, SummaryReceiptRow>) => {
      if (!props.column.editable) return <>{formatNumberV2(props.row.shelfLifeIssue)}</>

      if (!props.row.shelfLifeIssue || props.row.shelfLifeIssue === 0)
        return <div className='flex items-center justify-end h-full'><AiFillEdit className='text-[#007bff]' /></div>
      return <div className='flex items-center justify-between'>
        <AiFillEdit className=' text-[#007bff]' />
        <span className={` font-bold`}>{formatNumberV2(props.row.shelfLifeIssue)}</span>
      </div>
    },
    editable: true,
    editor(props: EditorProps<OrderReceipt, SummaryReceiptRow>) {
      return <NumberEditor onSubmit={(num) => props.onRowChange({...props.row, shelfLifeIssue: num}, true)} />
    },
    editorOptions: { editOnClick: true, commitOnOutsideClick: true }
  },
  { key: 'testIssue', name: 'Test', 
    minWidth: 55, width: 55,
    cellClass: ' text-right text-amber-600',
    headerCellClass: ' flex flex-col leading-[normal] items-end justify-center text-amber-600',
    headerRenderer: (props: HeaderRendererProps<OrderReceipt, SummaryReceiptRow>) => {
      return <>
        <span>T</span>
        <span className='text-[0.5rem] text-gray-700'>EA</span>
      </>
    },
    formatter: (props: FormatterProps<OrderReceipt, SummaryReceiptRow>) => {
      if (!props.column.editable) return <>{formatNumberV2(props.row.testIssue)}</>

      if (!props.row.testIssue || props.row.testIssue === 0)
        return <div className='flex items-center justify-end h-full'><AiFillEdit className='text-[#007bff]' /></div>
      return <div className='flex items-center justify-between'>
        <AiFillEdit className=' text-[#007bff]' />
        <span className={` font-bold`}>{formatNumberV2(props.row.testIssue)}</span>
      </div>
    },
    editable: true,
    editor(props: EditorProps<OrderReceipt, SummaryReceiptRow>) {
      return <NumberEditor onSubmit={(num) => props.onRowChange({...props.row, testIssue: num}, true)} />
    },
    editorOptions: { editOnClick: true, commitOnOutsideClick: true }
  },
  { key: 'reject', name: 'Reject', 
    cellClass: ' text-right text-red-500 flex flex-col leading-[normal] justify-center',
    headerCellClass: ' flex flex-col leading-[normal] items-end justify-center text-red-500',
    headerRenderer: (props: HeaderRendererProps<OrderReceipt, SummaryReceiptRow>) => {
      return <>
        <span>Reject</span>
        <span className='text-[0.5rem] text-gray-700'>EA</span>
      </>
    },
    formatter: (props: FormatterProps<OrderReceipt, SummaryReceiptRow>) => {
      if (!props.column.editable) return <>{formatNumberV2(props.row.reject)}</>

      if (!props.row.reject || props.row.reject === 0)
        return <div className='flex items-center justify-end h-full'><AiFillEdit className='text-[#007bff]' /></div>
      return <div className='flex items-center justify-between'>
        <AiFillEdit className=' text-[#007bff]' />
        <span className='font-bold '>{formatNumberV2(props.row.reject)} - {props.row.remark}</span>
      </div>
    },
    editable: true,
    editor(props: EditorProps<OrderReceipt, SummaryReceiptRow>) {
      return <InputReceiptReject props={props} />
    },
    editorOptions: { editOnClick: true, commitOnOutsideClick: true },
    summaryFormatter({ row }) {
      return <div className='font-bold text-right text-red-500'>Reject :{formatNumberV2(row.reject)} EA</div>
    }
  },
  { key: 'otherIssue', name: 'Other', 
    cellClass: ' text-right text-slate-500 flex flex-col leading-[normal] justify-center',
    headerCellClass: ' flex flex-col leading-[normal] items-end justify-center text-slate-500',
    headerRenderer: (props: HeaderRendererProps<OrderReceipt, SummaryReceiptRow>) => {
      return <>
        <span>Other Dep.</span>
        <span className='text-[0.5rem] text-gray-700'>EA</span>
      </>
    },
    formatter: (props: FormatterProps<OrderReceipt, SummaryReceiptRow>) => {
      if (!props.column.editable) return <>{formatNumberV2(props.row.otherIssue)}</>

      if (!props.row.otherIssue || props.row.otherIssue === 0)
        return <div className='flex items-center justify-end h-full'><AiFillEdit className='text-[#007bff]' /></div>
      return <div className='flex items-center justify-between'>
        <AiFillEdit className=' text-[#007bff]' />
        <span className={` font-bold`}>{formatNumberV2(props.row.otherIssue)} - {props.row.otherIssueNote}</span>        
      </div>
    },
    editable: true,
    editor(props: EditorProps<OrderReceipt, SummaryReceiptRow>) {
      return <InputOtherReq props={props} />
    },
    editorOptions: { editOnClick: true, commitOnOutsideClick: true },
    summaryFormatter({ row }) {
      return <div className='font-bold text-right'>แผนกอื่น :{formatNumberV2(row.other)} EA</div>
    }
  },
  { key: 'totalNet', name: 'Net',
    width: 100,
    cellClass: ' text-right',
    headerCellClass: ' flex flex-col leading-[normal] items-end justify-center',
    headerRenderer: (props: HeaderRendererProps<OrderReceipt, SummaryReceiptRow>) => {
      return <>
        <span>NET Qty.</span>
        <span className='text-[0.5rem] text-gray-700'>EA</span>
      </>
    },
    formatter: (props: FormatterProps<OrderReceipt, SummaryReceiptRow>) => {
      const { row } = props
      const sumOfQCSample = (row.retainedIssue || 0) + (row.microIssue || 0) + 
      (row.validateIssue || 0) + (row.shelfLifeIssue || 0) + (row.testIssue || 0) + 
      (row.otherIssue || 0)
      const net = (props.row.totalAmount || 0) - (props.row.reject || 0) - sumOfQCSample
      return <>{formatNumberV2(net)}</>
    },
    summaryFormatter({ row }) {
      return <div className="font-bold text-right ">{formatNumberV2(row.netEa)} EA</div>
    }
  }
]

export const OrderReceiptApprovalColumns: readonly Column<OrderReceipt>[] = [
  { key: 'productionOrderId', name: 'Order', width: 100,
    formatter: (props: FormatterProps<OrderReceipt>) => {
      const { row } = props
      return <Link 
        className="text-blue-500 cursor-pointer hover:underline"
        to={{
          pathname: `/${window.location.pathname.replace('/', '')}/${row.productionOrderId}`,
          // state: { orderReceipts: [] } // deprecated.
        }}
      >
        {row.productionOrderId}
      </Link>
    },
    frozen: true
  },
  { key: 'orderMaterialCode', name: 'Material', minWidth: 240,
    formatter: (props: FormatterProps<OrderReceipt>) => {
      const { row } = props
      return <div className='flex flex-col gap-1 leading-[normal] h-full justify-center'>
      <div className='flex flex-row items-center gap-1'>
        <p className='font-bold '>{row.orderMaterialDesc}</p>
        {row.isRework && <small className="flex items-end gap-1 px-2 py-1 font-bold text-red-500 bg-red-100 rounded-lg ">
          <ImCancelCircle size={12} />
          Rejected
        </small>}
      </div>
      <div className='flex items-center gap-1'>
        <p className='text-xs font-light'>{row.orderMaterialCode}</p>
        <h6 className='text-xs '>
          {row.orderType === 'REPACK' && <Badge variant="order-repack"><>{row.orderType}</></Badge>}
          {row.orderType === 'CLAIM' && <Badge variant="order-claim"><>{row.orderType}</></Badge>}
        </h6>
      </div>
    </div>
    },
    frozen: true
  },
  { key: 'orderQty', name: 'Qty.', width: 100, headerCellClass: ' text-right', cellClass: ' text-right',
    formatter: (props: FormatterProps<OrderReceipt>) => {
      const unit = props.row.productionOrderId.startsWith('9') ? 'EA' : 'KG'
      return <div className='text-right '>
        {formatNumberV2(props.row.orderQty)}<span className='text-[0.5rem] text-muted '> {unit}</span>
      </div>
    },
    frozen: true
  },
  { key: 'totalAmount', name: 'ผลิตได้ (EA)', headerCellClass: ' text-right', cellClass: ' text-right font-bold',
    width: 105,
    formatter: (props: FormatterProps<OrderReceipt>) => {
      return <>{formatNumberV2(props.row.totalAmount)}<span className='text-[0.5rem] text-muted '> EA</span></>
    }
  },
  {
    key: 'retainedIssue', name: 'Sample',
    headerCellClass: ' text-right ',  cellClass: ' text-right ',
    minWidth: 70, width: 70,
    formatter: (props: FormatterProps<OrderReceipt>) => {
      return <>{formatNumberV2(props.row.retainedIssue)}<span className='text-[0.5rem] text-muted '> EA</span></>
    }
  },
  { key: 'reject', name: 'Reject', 
    minWidth: 63, width: 63,
    headerCellClass: ' text-right ',  cellClass: ' text-right ',
    formatter: (props: FormatterProps<OrderReceipt>) => {
      if (!props.row.reject || props.row.reject === 0)
        return <>-</>
      return <>{formatNumberV2(props.row.reject)}<span className='text-[0.5rem] text-muted '> EA</span></>
    }
  },
  {
    key: 'otherIssue', name: 'Other', headerCellClass: ' text-right', cellClass: ' text-right',
    minWidth: 60, width: 60,
    formatter: (props: FormatterProps<OrderReceipt>) => {
      if (!props.row.otherIssue || props.row.otherIssue === 0)
        return <>-</>
      return <>{formatNumberV2(props.row.otherIssue)}<span className='text-[0.5rem] text-muted '> EA</span></>
    }
  },
  {
    key: 'approvedByPdDatetime', name: 'PD Approved By', width: 180,
    cellClass: "multi-line-row-data-grid",
    formatter: (props: FormatterProps<OrderReceipt>) => {
      if (props.row.approvedByPdBy) return displayByAndDatetime(props.row.approvedByPdBy, props.row.approvedByPdDatetime)
      return <>-</>
    }
  },
  {
    key: 'approvedByQcDatetime', name: 'QC Approved By', width: 180,
    cellClass: "multi-line-row-data-grid",
    formatter: (props: FormatterProps<OrderReceipt>) => {
      if (props.row.approvedByQcBy) return displayByAndDatetime(props.row.approvedByQcBy, props.row.approvedByQcDatetime)
      return <>-</>
    }
  },
  {
    key: 'approvedByWhDatetime', name: 'WH Approved By', width: 180,
    cellClass: "multi-line-row-data-grid",
    formatter: (props: FormatterProps<OrderReceipt>) => {
      if (props.row.approvedByWhBy) return displayByAndDatetime(props.row.approvedByWhBy, props.row.approvedByWhDatetime)
      return <>-</>
    }
  },
  {
    key: 'approvedByFaDatetime', name: 'FA Approved By', width: 180,
    cellClass: "multi-line-row-data-grid",
    formatter: (props: FormatterProps<OrderReceipt>) => {
      if (props.row.approvedByFaBy) return displayByAndDatetime(props.row.approvedByFaBy, props.row.approvedByFaDatetime)
      return <>-</>
    }
  },
  {
    key: 'detail', name: '', width: 100,
    sortable: false,
    formatter: (props: FormatterProps<OrderReceipt>) => {
      return <div className='flex items-center justify-center gap-1 text-blue-500'>
        <HiDocumentText />
        <Link 
          className="text-blue-500 cursor-pointer hover:underline"
          to={{
            pathname: `/${window.location.pathname.replace('/', '')}/${props.row.productionOrderId}`,
            // state: { orderReceipts: childRows }
          }}
        >
          ดูข้อมูล
        </Link>
      </div>
    }
  }
]

const displayByAndDatetime = (by: string, datetime: number | undefined | null) => {
  return <div className='flex flex-col items-center justify-center w-full gap-1 text-gray-500'>
    <span className='font-semibold '>{by}</span>
    <span className='text-xs italic font-light'>{formatDateFromUnix(datetime)}</span>
  </div>
}

export const OrderInspectionLogColumns: readonly Column<OrderInspectionLog>[] = [
  { key: 'workCenterId', name: 'Work Center',
    formatter: (props: FormatterProps<OrderInspectionLog>) => {
      const row = props.row
      return <>{row.workCenterId} - {row.workCenterDescription}</>
    },
  },
  { key: 'inspectionType', name: 'Form Type',
    width: 100,
    cellClass: 'flex items-center gap-2 ',
    formatter: (props: FormatterProps<OrderInspectionLog>) => {
      const row = props.row
      const splitFormType = row.inspectionType.split('_')
      return <>{splitFormType[1] === 'FILLING' ? <IoMdColorFill /> : <BiPackage />}{splitFormType[1]}</>
    },
  },
  { key: 'department', name: 'Dep.',
    minWidth: 60, width: 60,
    formatter: (props: FormatterProps<OrderInspectionLog>) => {
      const row = props.row
      const splitFormType = row.inspectionType.split('_')
      return <AmplifyBadge variation={splitFormType[0] === 'PD' ? 'success' : 'warning'} size="small">{splitFormType[0]}</AmplifyBadge>
    },
  },
  { key: 'avgFwtByLots', name: 'Avg. Filling Weight', 
    cellClass: "multi-line-row-data-grid",
    width: 200,
    formatter: (props: FormatterProps<OrderInspectionLog>) => {
      const { row } = props
      if (row.inspectionType !== 'PD_FILLING') return <>-</>

      const avgFwtByLots = row.avgFwtByLots ? (JSON.parse(row.avgFwtByLots) as AvgFwtByLot[]) : []

      return <div className='w-full space-y-1 '>
        <div className='space-y-3 '>
          {avgFwtByLots.map(fwt => <div key={fwt.BATCH_LOT}>&#8226; {fwt.BATCH_LOT}: <span className='italic font-bold'>{formatNumberDigit(fwt.AVG_FWT, 2)} g</span></div>)}
        </div>
        <p className='text-center work-center-edit'>Refresh</p>
      </div>
    },
  },
  { key: 'inspectionStatus', name: 'Status',
    width: 120,
    formatter: (props: FormatterProps<OrderInspectionLog>) => {
      const row = props.row
      if (row.id === '') return <p className="font-bold text-red-600 ">ยังไม่เริ่มนับ</p>
      
      if (row.inspectionStatus === 'TESTING' ) return <p className="font-bold text-amber-600 ">กำลังนับ</p>
      if (row.inspectionStatus === 'SUBMITTED' ) return <p className="flex items-center gap-2 font-bold text-green-600 ">
        <FaCheckCircle className="text-green-500 " size={16}/>ส่งงานแล้ว
      </p>
      if (row.inspectionStatus === 'APPROVED' ) return <p className="font-bold text-green-900 ">อนุมัติแล้ว</p>
      return <></>
    },
  },
  { key: 'submittedDatetime', name: 'Submitted At',
    width: 150,
    formatter: (props: FormatterProps<OrderInspectionLog>) => {
      const row = props.row
      return <>{formatDateFromUnix(row.submittedDatetime)}</>
    },
  },
  { key: 'submittedBy', name: 'Submitted By',
    width: 160,
    formatter: (props: FormatterProps<OrderInspectionLog>) => {
      const row = props.row
      return <>{row.submittedBy}</>
    },
  },
  { key: 'link', name: 'Doc',
    width: 80,
    formatter: (props: FormatterProps<OrderInspectionLog>) => {
      const row = props.row
      return <a href={row.formUrl} target="_blank" rel="noopener noreferrer" className='flex items-center gap-2 work-center-edit '>
        <SiMicrosoftexcel className='text-green-600 ' /> กดดู
      </a>
    }
  }
]

export const WorkCenterColumns: readonly Column<WorkCenter>[] = [
  { key: 'id', name: 'ID', width: 80 },
  { key: 'description', name: 'Description' },
  { key: 'machineCategory', name: 'Category', width: 150,
  formatter: (props: FormatterProps<WorkCenter>) => {
    return <div className='flex items-center justify-between h-full cursor-pointer'>
        {props.row.machineCategory || '-'}
        <AiFillEdit className=' text-[#007bff]' />
      </div>
  }, editor(props: EditorProps<WorkCenter>) {
    const { row, onRowChange } = props
    return <div className='flex items-center justify-center w-full h-full'>
      <select title='select'
        value={row.machineCategory || 'ไม่ระบุ'}
        className='p-1 border border-gray-400 rounded-md'
        onChange={(e) => {
          const updatedRow = { ...row, 
            machineCategory: e.currentTarget.value as MachineCategory
          }
          onRowChange(updatedRow, true)
        }}
        onKeyDown={(e) => e.stopPropagation()}
      >
        {Object.keys(MachineCategory).map( category => <option key={category} value={category}>{category}</option>)}
      </select>
    </div>
  }, editorOptions: {
    editOnClick: true, commitOnOutsideClick: true
  } },
  { key: 'active', name: 'Active', width: 100,
    cellClass: ' flex items-center justify-center',
    formatter: (props: FormatterProps<WorkCenter>) => {
      return props.row.active ? <AiFillCheckCircle size={20} className='text-green-600 ' /> : <AiFillCloseCircle size={20} className='text-red-600 ' />
    },
    editor(props: EditorProps<WorkCenter>) {
      const { row, onRowChange } = props
      return <div className='flex items-center justify-center w-full h-full'>
        <select title='select'
          value={row.active ? 'ACTIVE' : 'INACTIVE'}
          className='p-1 border border-gray-400 rounded-md'
          onChange={(e) => {
            const updatedRow = { ...row, 
              active: e.currentTarget.value === 'ACTIVE' ? true : false
            }
            onRowChange(updatedRow, true)
          }}
          onKeyDown={(e) => e.stopPropagation()}
        >
          {['ACTIVE', 'INACTIVE'].map( sts => <option key={sts} value={sts}>{sts}</option>)}
        </select>
      </div>
    }, editorOptions: {
      editOnClick: true, commitOnOutsideClick: true
    } 
  },
  // isRequiredManHour: Boolean @default(value: "true")
  // isRequiredBatchLotNo: Boolean @default(value: "true")
  // isRequiredFGInspect: Boolean @default(value: "false")
  // machineCategory: MachineCategory
]

export const getDeleteInspectionColumn = (readOnly: boolean, department: Department) : Column<OrderInspectionLog> => {
  let dep = ''
  if (department === Department.PRODUCTION) dep = 'PD'
  if (department === Department.QC) dep = 'QC'
  
  return {
    key: 'delete', name: '', minWidth: 50, width: 50,
    cellClass: `flex items-center justify-center cursor-pointer`,
    formatter: (props: FormatterProps<OrderInspectionLog>) => {
      const editable = !readOnly && props.row.inspectionType.startsWith(dep)
      return props.row._deleted || !editable ? <></> : <MdOutlineCancel color='red' size={20}/>
    },
    editable: row => !readOnly && row.inspectionType.startsWith(dep),
    editor : (props: EditorProps<OrderInspectionLog>) => {
      return <RemoveUnusedInspectionEditor props={props} />
    },
    editorOptions: {
      editOnClick: true
    }
  }
}